import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useCampaignStore } from '@/stores';
import { Link, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Camera, CheckCircle2, ChevronRight, CircleMinus, Lock, Pencil, RotateCcw, Trash2, Undo2 } from 'lucide-react';
import { useReducedMotion } from '@/hooks';
import { useOwedReportWindow } from '@/hooks/finance/useOwedReportWindow';
import {
  updateFinanceContactRequest,
  assertNever,
  PreflightCopy,
  resolvedAsLabel,
  expenditureCategoryLabel,
  contributionTypeLabel,
  contributionDesignationOptions,
  requiresInKindNature,
  requiresInKindValuationBasis,
  isClosedInKindValuationBasis,
  isInKindCaptureComplete,
  IN_KIND_NATURE_OPTIONS,
  IN_KIND_VALUATION_BASIS_OPTIONS,
  InKindNature,
  ContactKind,
  ReceiptNature,
  ExpenditureCategory,
  ExpenditureHintProvenance,
  ContactType,
  AmendmentModel,
  missingFieldsLabel,
  type BankTransactionClassificationHintDto,
  type BankTransactionDepositHintDto,
  type CreateFinanceContactRequest,
  type DuplicateContactFlagDto,
  type ProcessorFeeStatusLine,
  type SortInboxBankTransactionDto,
  type TriageItem,
  type TriageItemStatus,
  ErrorCode,
  isStaleWrite,
} from '@signalvote/shared';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { PendingButton } from '@/components/ui/pending-button';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useSortInbox,
  useDuplicateContactFlags,
  useOrganizationTypeConflicts,
  useConfirmContribution,
  useUpdateContribution,
  useUpdateExpenditure,
  useUnconfirmContribution,
  useUnlinkRefund,
  useRevokeRefundStandalone,
  useUnconfirmExpenditure,
  useConfirmExpenditure,
  useMarkContributionReported,
  useUnmarkContributionReported,
  useMarkExpenditureReported,
  useUnmarkExpenditureReported,
  useUpdateFinanceContact,
  FinanceContactUpdateError,
  ExpenditureConfirmError,
  useExcludeBankTransactionFromReport,
  useEditContributionRefund,
  useReopenBankTransaction,
  useContributionCheckImage,
  useFinanceContacts,
  useCreateFinanceContact,
  useVoidContribution,
  useUnvoidContribution,
  useVoidExpenditure,
  useUnvoidExpenditure,
  useDeleteContribution,
  useDeleteExpenditure,
  useDeleteContributionRefund,
  useDeleteBankTransaction,
  StagedRowUpdateError,
  BankTransactionClassifyError,
  useFinanceSettings,
  useComplianceProfile,
  useFederalFlaggedDisbursementPurposes,
  useClassifyBankTransactionAsExpenditure,
} from '@/hooks/queries';
import { acceptHintRequest } from '@/lib/finance/classificationHint';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { findScrollParent } from '@/lib/scrollParent';
import { usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import { stragglerAmendHint } from '@/components/finance/reports/amendmentWorkflowHint';
import {
  useQuickAction,
  RowError,
  REFRESH_PROMPT,
  SAVE_FAILED_MESSAGE,
  useJustMarkedReportedStore,
  useIsJustMarkedReported,
  useIsLastAcceptedHint,
  useLastAcceptedHintStore,
} from './rowAction';
import { markReportedElsewhereCopy } from './markReportedCopy';
import { rowLockCopy } from './rowLockCopy';
import { moneyRowAffordances } from './rowFreezeAffordances';
import { formatAmount, formatDate } from '@/components/finance/utils';
import { BankLineInfoHeader } from './BankLineInfoHeader';
import { triageItemDirection } from '@/lib/finance/triageDirection';
import { isNotReportedResolution } from '@/lib/finance/triageResolved';
import {
  pickJustCompletedId,
  snapshotStatuses,
  resolveClassifyFollowUp,
} from '@/lib/finance/ledgerCompletionFlash';
import { nextMonthName, nextMonthLabel } from '@/lib/finance/periodLabels';
import {
  BankTransactionClassificationPanel,
} from '@/components/finance/classification';
import { AddressSuggestInput } from '@/components/finance/shared/classification';
import { SettlesDebtPicker } from '@/components/finance/debts';
import { RepaysLoanPicker } from '@/components/finance/loans';
import {
  ClassifyOrganizationBody,
  ContributorShapeBody,
  ContributorShapeNudgeBody,
  MissingInfoBody,
  OrphanRefundBody,
} from './triage';
import { InlineContactForm, transferQualificationSummary } from '@/components/finance/entry/entryParts';
import {
  FederalTransferQualification,
  FEDERAL_TRANSFER_QUALIFICATION_OPTIONS,
} from '@signalvote/shared';
import { RefundDialog } from './RefundDialog';
import { ResolutionChainView } from '../resolutions';
import { ProcessorFeeLedgerRows, isFeeLinePinnable } from './ProcessorFeeLedgerRow';
import { DuplicateResolvePanel } from './DuplicateResolvePanel';
import {
  financeInlineEditTriggerClass,
  financeFormInputClass,
  financeFormSelectClass,
  financeTextActionClass,
} from '@/components/finance/shared/financeControlClasses';
import {
  FiledReportAmendLink,
  filedReportRefusal,
  plainRefusal,
  type DeleteRefusal,
} from '@/components/finance/shared';
import { reportedOnFileNote } from './reportedOnFileNote';

// ─── Period-range helpers (used by 3-bucket rendering) ──────────────────────

// ────────────────────────────────────────────────────────────────────────────

/**
 * Close-the-period "Register" (design doc 2026-06-19): the all-time ledger
 * reshaped as a check-register. Every money movement is one scannable row led
 * by a single `ReadyCheckbox` = "ready to report", with a row-number gutter,
 * a dedicated Category column, and spreadsheet gridlines. One control drives
 * readiness; the rest of the row is scannable.
 *
 * This is a PRESENTATION reshape over the same `useSortInbox` data, the same
 * ~20 mutations, and the same per-kind expansion bodies — not a rebuild. The
 * leading checkbox dispatches on the server-computed `status` +
 * `actionNeeded` + `resolvedAs` triple (see `ReadyCheckbox`).
 */

// ADR-026 (PR-B) — cap the deep-link auto-page loop at ~5 pages / 500 rows
// (page size 100). A deep link into deep history pages toward the row up to
// this many fetches, then lands at the top with a non-silent hint rather than
// paging the entire ledger.
const MAX_FOCUS_AUTO_PAGES = 5;

// ADR-026 (PR-D) — the Type filter's options, in ledger order. Each value is the
// raw entity kind (the PascalCase wire discriminator the API filters on); the
// label goes through `kindLabel` so `BankLine` renders as "Bank transaction".
const FILTER_KIND_ORDER: TriageItem['kind'][] = [
  'Contribution',
  'Expenditure',
  'BankLine',
  'Refund',
];

// ADR-026 (PR-C) — deep-scroll robustness. The accumulated display stream is
// flattened into ONE list of `RenderRow`s (section headers + data rows) and the
// virtualizer windows over that single list so the DOM `<tr>` count stays roughly
// constant no matter how many pages a treasurer has scrolled through (a
// Doug-Jones-scale ~8,400-row all-time ledger). See § 2/§ 6.
type RenderRow =
  // A section marker (next-period toggle / in-period header / earlier divider).
  // Rendered as one `<tr>` with a `<td colSpan={7}>` carrying `render()`. The
  // optional `testid` lands on that `<tr>` (the "older / earlier" dividers keep
  // `triage-ledger-older-divider`); `cellClassName`/`rowClassName` carry the
  // section's spacing onto the `<td>`/`<tr>` exactly as the pre-virtualization
  // markup did (so the same border/padding classes still land there).
  | {
      type: 'header';
      key: string;
      testid?: string;
      rowClassName?: string;
      cellClassName?: string;
      render: () => ReactNode;
    }
  // One data row — rendered as the single merged `TriageLedgerRow` `<tr>`.
  | { type: 'item'; key: string; item: TriageItem; periodNote?: string };

// The seed height (px) for a not-yet-measured row — the real height of a
// COLLAPSED 7-column row (§ 4): shadcn cell padding (p-2 = 16px vertical) around
// the two-line body. `measureElement` corrects it to the true (incl. expanded)
// height as each row mounts. Nonzero on purpose — so `totalSize` is never 0 even
// in jsdom, which is why the degradation signal is a zero-height CONTAINER, not a
// zero total size (§ jsdom degradation).
const LEDGER_ROW_ESTIMATE_PX = 52;

// Rows kept mounted just outside the visible window so scrolling never flashes
// blank rows before they mount.
const LEDGER_VIRTUAL_OVERSCAN = 8;

export interface TriageLedgerProps {
  /**
   * The calculated processor-fee lines (from `sortInbox.processorFeeLines` /
   * `preflight.processorFeeLines`, OWED-period-scoped — the period being filed), pinned as
   * render-only dashed register rows at the TOP of the in-period (owed) group. WEIGHTLESS:
   * never enter `items`, so they are excluded from every count, total, cash-on-hand, selection,
   * and the file gate. Only the armed (owed) lines render, each as an always-actionable Record
   * pin; booking is period-scoped (the server resolves the period + stamps the date), so no
   * report id / period end is threaded in. Empty by default (the close hub passes them; other
   * mounts render no fee rows).
   */
  processorFeeLines?: ProcessorFeeStatusLine[];
}

// Stable empty default for `processorFeeLines` (root cause #1). A fresh `[]` in
// the destructure default would give every render a new array identity, busting
// the `pinnedFeeRow` useCallback → the `renderRows` useMemo → the whole bucketed
// row list, on every render. A module-level constant keeps that chain memoized
// for the common (no-fee) mounts.
const EMPTY_PROCESSOR_FEE_LINES: ProcessorFeeStatusLine[] = [];

export function TriageLedger({
  processorFeeLines = EMPTY_PROCESSOR_FEE_LINES,
}: TriageLedgerProps = {}) {
  // ADR-026 (PR-D) — the display filter bar's LOCAL state (Type kinds + payee
  // search). NOT URL-persisted / not in Zustand / not in the query cache (URL
  // persistence is a named deferred follow-up). The filter narrows only the
  // display query below; the NeedsAction work feed is never filtered.
  // Single-select Type filter: one kind at a time (`''` = no type filter).
  const [selectedKind, setSelectedKind] = useState<TriageItem['kind'] | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Hand-rolled ~300 ms debounce: the payee search only reaches the query (a new
  // page-1 fetch) after typing settles. No shared debounce util exists.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  // The single "is a filter narrowing the display?" predicate. Reads the
  // DEBOUNCED query (not the raw input) so "active", the query key, and the
  // "Showing N of M" indicator all flip together — never mid-keystroke.
  const filterActive = selectedKind !== '' || debouncedQuery.length > 0;
  // The stable object fed to the hook + key. The server still takes a list of
  // kinds; single-select sends 0 or 1, keeping the wire contract unchanged.
  const filter = useMemo(
    () => ({ kinds: selectedKind ? [selectedKind] : [], q: debouncedQuery }),
    [selectedKind, debouncedQuery],
  );

  // ADR-026 (PR-B) — the display is a keyset-cursor useInfiniteQuery. Buckets
  // are computed over the ACCUMULATED pages; envelope scalars (period bounds /
  // hasOpenPeriod) are period-scoped, identical on every page, so they read
  // from page 0. `fetchNextPage` is reference-stable (destructured, never the
  // whole query object) so it is safe in effect/observer deps. The filter
  // (ADR-026 PR-D) re-keys this to a distinct cache entry that starts at page 1.
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSortInbox(filter);

  // Open duplicate-donor flags for the campaign, indexed by BOTH sides' contact
  // id → the flag. A flagged Contribution row shows a "possible duplicate" badge
  // and, when expanded, the inline resolve panel. Independent of the ledger data
  // (its own cid()-scoped query); memoized so the map reference is stable
  // between benign re-renders.
  const { data: duplicateFlags } = useDuplicateContactFlags();
  const flagByContactId = useMemo(() => {
    const map = new Map<string, DuplicateContactFlagDto>();
    for (const flag of duplicateFlags?.flags ?? []) {
      map.set(flag.contactA.contactId, flag);
      map.set(flag.contactB.contactId, flag);
    }
    return map;
  }, [duplicateFlags]);

  // Contact ids carrying an unresolved contact-merge OrganizationType conflict (#350). A row whose
  // contact (contributor OR payee) is in this set shows a badge linking to the contact page, where
  // the one-tap fix-it alert lives — mirroring the duplicate badge as an always-on data-hygiene
  // nudge (the jurisdiction-aware filtering is on the filing preflight, not the badge).
  const { data: orgTypeConflicts } = useOrganizationTypeConflicts();
  const orgTypeConflictContactIds = useMemo(
    () => new Set((orgTypeConflicts?.conflicts ?? []).map((c) => c.contactId)),
    [orgTypeConflicts],
  );
  // Read ONCE here, not per row. The verdict rides down to the cell as a plain boolean so
  // the memoized row still bails out of re-render when the set changes but ITS row's answer
  // didn't — the same shape as hasOrgTypeConflict above.
  const flaggedPurposeIds = useFederalFlaggedDisbursementPurposes();

  // One row expanded at a time — which form the expansion shows is
  // unambiguous per kind, so the id alone is the whole expansion state.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Latest committed `expandedId`, read by the stable toggle/collapse callbacks
  // below so their identity never changes on expand/collapse (root cause #2).
  // Keying those callbacks on `expandedId` (their old deps) gave them a fresh
  // identity on the MOST COMMON interaction, busting React.memo on every row.
  // Written during render (the idempotent "latest value" ref pattern) and read
  // only inside event handlers — never during render.
  const expandedIdRef = useRef<string | null>(null);
  expandedIdRef.current = expandedId;
  // 3-bucket mode: whether the "later period" collapsed section is expanded.
  const [laterExpanded, setLaterExpanded] = useState(false);
  // The owed report's period window (null = caught up / no owed report).
  // When set, the ledger uses 3 buckets (later / in-period / earlier).
  // When null, the original 2-bucket fallback applies. While health is
  // unanswered the owed report is unknown, so the rows render unscoped: the
  // calendar period never stands in for the owed one.
  const owedRead = useOwedReportWindow();
  const owedWindow = owedRead.status === 'ready' ? owedRead.data : null;
  const owedUnknown = owedRead.status === 'ready' ? null : owedRead.status === 'idle' ? 'loading' : owedRead.status;

  // Deep link from a report-detail row: it navigates to the close ledger with
  // the transaction's id in the search (`?contributionId=` / `?expenditureId=`,
  // mirroring the CloseHubShell idiom). Each `TriageItem.id` IS the underlying
  // entity id, so the deep-link id matches a row's `item.id` directly.
  const search = useSearch({ strict: false }) as {
    contributionId?: unknown;
    expenditureId?: unknown;
  };
  const focusId =
    (typeof search.expenditureId === 'string' && search.expenditureId) ||
    (typeof search.contributionId === 'string' && search.contributionId) ||
    null;

  // The row to highlight; cleared after a brief fade so the ring doesn't stick.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // The row that just collapsed after being inspected. It gets a short row-memory
  // mark + focus restoration so closing a large inline body does not make the
  // treasurer lose their place when the ledger reflows.
  const [recentlyCollapsedId, setRecentlyCollapsedId] = useState<string | null>(null);
  // The collapsed row whose focus/scroll restoration should run after React
  // commits the collapsed layout. Keeping DOM work in an effect avoids side
  // effects inside state updaters.
  const [focusAfterCollapseId, setFocusAfterCollapseId] = useState<string | null>(null);
  // The row that just settled into Done — flashed with a one-shot success wash
  // so the treasurer keeps their place after finishing a row (it collapses and
  // leaves the NeedsAction queue, which is easy to lose). Derived from the
  // status transition in the data (see the effect below), not a callback, so it
  // fires for EVERY completion path (fill+confirm, confirm, classify) uniformly.
  const [justCompletedId, setJustCompletedId] = useState<string | null>(null);
  // Prior (id -> status) snapshot the completion diff runs against.
  const prevStatusRef = useRef<Map<string, TriageItemStatus>>(new Map());
  // The entity a classify just created, pending its arrival in the refetch.
  // When it lands, if it still needs work we open its editor so the treasurer
  // continues in place instead of the row collapsing and forcing a re-click.
  const [followUpId, setFollowUpId] = useState<string | null>(null);
  // One-shot guard: remembers which focus id we've already acted on so the
  // effect can't re-fire on unrelated re-renders (expand/collapse, edits).
  const focusedRef = useRef<string | null>(null);
  // Counts the deep-link auto-page loop's fetchNextPage calls (capped ~5 pages /
  // 500 rows) so a deep link into deep history doesn't page the whole ledger.
  // Distinct from focusedRef (which stores WHICH id, not a count).
  const autoPageAttemptsRef = useRef(0);
  // True once a deep-linked row can't be found within the cap — drives the
  // non-silent "earlier period" hint instead of a silent no-op.
  const [focusMissed, setFocusMissed] = useState(false);
  // The infinite-scroll sentinel node (observed to fetch the next page).
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // ADR-026 (PR-C) — the ledger root (the virtual list container) + the resolved
  // scroll element the virtualizer windows against (nearest scrollable ancestor,
  // § scroll context). `scrollEl` is state so the virtualizer re-reads it once
  // it is resolved at mount.
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  // The list's offset within the scroll element, so page-scroll windowing is
  // correct (the `<thead>` and any content above the `<tbody>` sit inside the
  // same scroller). Recomputed at mount alongside `scrollEl`.
  const [scrollMargin, setScrollMargin] = useState(0);

  // Accumulated rows across all loaded pages. Memoized on `data.pages` so the
  // reference is stable between benign re-renders (the deep-link/highlight
  // effect must re-run only when a page is actually appended, not on every
  // render — else the 2 s highlight timer would be cleared prematurely).
  const items = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data?.pages],
  );

  // Flash a row the instant it settles into Done. Keyed on `items` (memoized on
  // data.pages) so it runs only when the list actually changes, diffing the new
  // statuses against the prior snapshot — a benign refetch with no transition
  // stays quiet, and the first snapshot (empty prev) never flashes on load.
  useEffect(() => {
    const completed = pickJustCompletedId(prevStatusRef.current, items);
    prevStatusRef.current = snapshotStatuses(items);
    if (completed) setJustCompletedId(completed);
  }, [items]);

  // The success wash clears itself ~1 s after it is set (the animation is 900ms),
  // decoupled from the transition effect so it can't fight the diff.
  useEffect(() => {
    if (!justCompletedId) return;
    const timer = setTimeout(() => setJustCompletedId(null), 1000);
    return () => clearTimeout(timer);
  }, [justCompletedId]);

  // Carry the flow into the row a classify just created: once it appears in the
  // refetch, open its editor if it still needs work (over-limit payee address,
  // itemized donor info); leave it collapsed if it came out Done (the wash
  // reads). Waits across refetches until the new row actually lands.
  useEffect(() => {
    if (!followUpId) return;
    const { resolved, expandId } = resolveClassifyFollowUp(followUpId, items);
    if (!resolved) return;
    setExpandedId(expandId);
    setFollowUpId(null);
  }, [items, followUpId]);

  // After a classify: collapse the (now classified) bank line and remember the
  // new entity so the effect above opens it once the refetch lands, if it still
  // needs work. The invalidate itself fires in the panel's onClassified handler.
  const handleClassifyFollowUp = useCallback((newEntityId?: string) => {
    setExpandedId(null);
    if (newEntityId) setFollowUpId(newEntityId);
  }, []);
  // Whether there is anything to render (also the null-guard). Kept as a value
  // the sentinel effect can depend on, so the observer attaches the moment the
  // sentinel mounts after a null loading render.
  const hasRows = !isLoading && !isError && !!data && items.length > 0;
  // ADR-026 (PR-D) — filter active but the (loaded) response has zero rows. FALSE
  // during the post-filter loading/error window (it requires !isLoading, !isError,
  // !!data), so a re-keyed query that is still fetching never flashes the
  // empty-match message for a filter that will return rows.
  const filteredEmpty =
    filterActive && !isLoading && !isError && !!data && items.length === 0;

  // ADR-026 (PR-C) — the single flat `RenderRow[]` the virtualizer covers.
  //
  // Append-at-the-bottom invariant: the keyset-cursor pages (ADR-026 PR-B) are
  // append-only and always OLDER (each `nextCursor` page is strictly below the
  // last row), so this list grows only at the BOTTOM of the stream. That much is
  // true — but we do NOT
  // assume fixed in-period indices: a single period larger than one 100-row page
  // splits its in-period rows across pages, so later pages LENGTHEN the in-period
  // block and push the in-period header / earlier divider to higher indices.
  // Therefore every marker's (and `scrollToIndex` target's) index is resolved by
  // looking it up in this FRESHLY BUILT array each render — never a memoized
  // constant position.
  const first = data?.pages[0];

  // Finding 10 — the pinned calculated-processor-fee rows, injected as a single
  // `header`-type RenderRow at the TOP of the current/open period group (under
  // the period header, above the transaction rows). A `header` row — NEVER an
  // `item` — so it can never flow into `items`, the counts, selection, or the
  // file gate; it stays structurally weightless like the retired banner. The
  // component self-suppresses (returns null) when no processor has anything to
  // say, in which case this header renders an empty cell and the ledger pins
  // nothing visible.
  const pinnedFeeRow = useCallback(
    (): RenderRow => ({
      type: 'header',
      key: 'pinned-processor-fee',
      rowClassName: 'hover:bg-transparent',
      cellClassName: 'p-0',
      render: () => (
        <div className="px-2 pb-1 pt-1.5">
          <ProcessorFeeLedgerRows processorFeeLines={processorFeeLines} />
        </div>
      ),
    }),
    [processorFeeLines],
  );
  // Whether any processor line is worth pinning — mirrors the component's own pinnable
  // predicate (an armed Record pin OR a muted forecast heading, ADR-045) so we don't emit an
  // empty header row (which would still occupy a virtualizer index for nothing) for a
  // settled/not-yet-owed fee, nor drop a forecast row the component would render.
  const hasPinnedFee = processorFeeLines.some(isFeeLinePinnable);

  const renderRows = useMemo<RenderRow[]>(() => {
    if (items.length === 0) return [];
    const rows: RenderRow[] = [];
    if (owedWindow) {
      // ── 3-bucket mode: later / in-period / earlier ──────────────────────
      const laterItems = items.filter((i) => i.date > owedWindow.end);
      const inPeriodItems = items.filter(
        (i) => i.date >= owedWindow.start && i.date <= owedWindow.end,
      );
      const earlierItems = items.filter((i) => i.date < owedWindow.start);
      const periodTag = `${nextMonthName(owedWindow.end)} · not on this report`;

      if (laterItems.length > 0) {
        // Collapsed "next period" toggle. Later-period rows are their OWN `item`
        // RenderRows (§ 2) appended after it when expanded — NOT nested inside
        // this header's render(), so each is its own virtualizer index.
        rows.push({
          type: 'header',
          key: 'later-toggle',
          rowClassName: 'hover:bg-slate-50/50',
          cellClassName: 'border-b border-border py-2',
          render: () => (
            <button
              type="button"
              data-testid="ledger-next-period-bucket"
              aria-expanded={laterExpanded}
              onClick={() => setLaterExpanded((e) => !e)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded px-1 py-0.5',
                'text-[11px] font-medium text-slate-500 font-mono tracking-[.04em]',
                'hover:bg-slate-100 hover:text-slate-700',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              <ChevronRight
                className={cn(
                  'h-3.5 w-3.5 shrink-0 transition-transform duration-150',
                  laterExpanded && 'rotate-90',
                )}
                aria-hidden
              />
              {nextMonthLabel(owedWindow.end)} · next period ·{' '}
              {laterItems.length === 1
                ? '1 transaction belongs'
                : `${laterItems.length} transactions belong`}
            </button>
          ),
        });
        if (laterExpanded) {
          for (const item of laterItems) {
            rows.push({ type: 'item', key: item.id, item, periodNote: periodTag });
          }
        }
      }

      // In-period section header (3-bucket only). Keeps the widened top border
      // when a collapsed next-period bucket sits above it. ADR-026 (PR-D) — only
      // render it when its bucket has rows, so a filter that removes every
      // in-period row can't leave a bare header (the later toggle + earlier
      // divider already guard on length).
      if (inPeriodItems.length > 0) {
        rows.push({
          type: 'header',
          key: 'in-period-header',
          rowClassName: 'hover:bg-transparent',
          cellClassName: cn(
            'pb-2 pt-3 text-left text-[11px] font-semibold',
            'font-mono tracking-[.06em] text-slate-500 uppercase',
            laterItems.length > 0 && 'border-t border-border pt-6',
          ),
          render: () => <>{owedWindow.periodLabel}</>,
        });
        // Finding 10 — pin the calculated-fee rows at the TOP of the in-period
        // group, directly under the period header and above the transactions.
        if (hasPinnedFee) rows.push(pinnedFeeRow());
        for (const item of inPeriodItems) {
          rows.push({ type: 'item', key: item.id, item });
        }
      }

      if (earlierItems.length > 0) {
        rows.push({
          type: 'header',
          key: 'earlier-divider',
          testid: 'triage-ledger-older-divider',
          rowClassName: 'hover:bg-transparent',
          cellClassName:
            'border-t border-border pt-9 pb-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
          render: () => <>Earlier periods</>,
        });
        for (const item of earlierItems) {
          rows.push({ type: 'item', key: item.id, item });
        }
      }
    } else if (owedUnknown) {
      // ── Unscoped (owed report unknown) ──────────────────────────────────
      if (owedUnknown !== 'loading') {
        rows.push({
          type: 'header',
          key: 'owed-unknown',
          testid: 'triage-ledger-owed-unknown',
          rowClassName: 'hover:bg-transparent',
          cellClassName: 'pb-2 pt-3 text-left text-[12.5px] text-amber-900',
          render: () => (
            <>
              {owedUnknown === 'offline'
                ? 'You’re offline, so these rows aren’t split by report period yet.'
                : 'We couldn’t load which report you owe, so these rows aren’t split by report period.'}
            </>
          ),
        });
      }
      if (hasPinnedFee && !!first && first.hasOpenPeriod) rows.push(pinnedFeeRow());
      for (const item of items) {
        rows.push({ type: 'item', key: item.id, item });
      }
    } else {
      // ── 2-bucket fallback (no owed window) ──────────────────────────────
      // No explicit current-period header here: the open period's rows lead the
      // stream (before the "Older than this period" divider). Pin the calculated
      // -fee rows at the very TOP of that leading block, but only when a period
      // is actually open (no open period → nothing to record into).
      if (hasPinnedFee && !!first && first.hasOpenPeriod) {
        rows.push(pinnedFeeRow());
      }
      items.forEach((item, index) => {
        // ISO yyyy-MM-dd compares lexicographically. Items arrive date-desc, so
        // the first older-than-period item marks the boundary. Suppressed
        // entirely when no period is open. `first` is authoritative from page 0.
        const isOlder = !!first && item.date < first.periodStart;
        const prev = items[index - 1];
        const isBoundary =
          !!first &&
          first.hasOpenPeriod &&
          isOlder &&
          (!prev || prev.date >= first.periodStart);
        if (isBoundary) {
          rows.push({
            type: 'header',
            key: 'older-divider',
            testid: 'triage-ledger-older-divider',
            rowClassName: 'hover:bg-transparent',
            cellClassName:
              'border-t border-border pt-9 pb-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
            render: () => <>Older than this period</>,
          });
        }
        rows.push({ type: 'item', key: item.id, item });
      });
    }
    return rows;
  }, [items, owedWindow, owedUnknown, laterExpanded, first, hasPinnedFee, pinnedFeeRow]);

  // The virtualizer over the whole flat list (§ 6). `estimateSize` seeds a
  // nonzero collapsed height; `measureElement` (ResizeObserver-backed default)
  // learns each row's real height — including an expanded row, which is one
  // measured element now (§ 5) — so the spacers reflow on expand/collapse.
  const virtualizer = useVirtualizer({
    count: renderRows.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => LEDGER_ROW_ESTIMATE_PX,
    measureElement: (el) =>
      (el as HTMLElement)?.getBoundingClientRect().height ?? LEDGER_ROW_ESTIMATE_PX,
    overscan: LEDGER_VIRTUAL_OVERSCAN,
    scrollMargin,
  });

  // Resolve the scroll element (nearest scrollable ancestor) + the list's offset
  // within it at mount, once the list container exists. `useLayoutEffect` so it
  // runs before paint and the virtualizer reads a settled scroll element.
  useLayoutEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;
    const resolved = findScrollParent(container);
    setScrollEl(resolved);
    if (resolved) {
      const containerTop = container.getBoundingClientRect().top;
      const scrollerTop = resolved.getBoundingClientRect().top;
      setScrollMargin(containerTop - scrollerTop + resolved.scrollTop);
    }
    // Re-resolve when the render list first becomes non-empty (the container
    // mounts after the null loading render).
  }, [hasRows]);

  // jsdom (and any not-yet-laid-out / hidden mount) has NO layout engine — the
  // scroll container reports zero height, so `measureElement` yields zeros and a
  // live virtualizer would window against nothing. The honest signal is a
  // zero-height CONTAINER (NOT a zero total size — `estimateSize` is nonzero, so
  // `totalSize` is never 0). When it is zero, render EVERY `RenderRow` in order
  // with NO spacer `<tr>`s — keeping `tbody tr` = one `<tr>` per item/header and
  // every existing `getByTestId` lookup green (§ jsdom degradation).
  const renderAll = scrollEl == null || scrollEl.clientHeight === 0;

  // A new deep-link target resets the capped auto-page search.
  useEffect(() => {
    autoPageAttemptsRef.current = 0;
    setFocusMissed(false);
  }, [focusId]);

  useEffect(() => {
    if (!focusId) return;
    // Wait until the scroll element is resolved (the layout effect runs before
    // this passive effect's re-run). Acting on the very first null-scrollEl
    // commit would see the transient render-all DOM and always take the
    // scrollIntoView branch — deferring one commit lets the settled path
    // (render-all under a zero-height container → scrollIntoView; windowed under
    // a real container → scrollToIndex for an off-window row) decide correctly.
    if (scrollEl == null) return;
    // One-shot per navigation: skip if we've already focused this exact id.
    if (focusedRef.current === focusId) return;

    // Found on an accumulated page → run the existing focus behavior. The 2 s
    // highlight-clear lives in its own effect (below) keyed on `highlightId`,
    // so a benign re-run of THIS effect within the window (e.g. a background
    // page append) can't clear the ring prematurely via the one-shot return.
    if (items.some((it) => it.id === focusId)) {
      // A deep link to a COLLAPSED later-period row (a `laterItems` row while
      // `laterExpanded` is false) has NO flat-list index — those rows are only
      // emitted when expanded (§ 2). Expand the next-period bucket FIRST, then
      // let the effect re-run and resolve the index next render (do NOT set the
      // one-shot guard yet, so it re-fires once the row is in `renderRows`).
      if (
        owedWindow &&
        !laterExpanded &&
        items.some((it) => it.id === focusId && it.date > owedWindow.end)
      ) {
        setLaterExpanded(true);
        return;
      }

      focusedRef.current = focusId;
      setFocusMissed(false);
      setExpandedId(focusId);
      setHighlightId(focusId);

      // ADR-026 (PR-C) — keep `scrollIntoView` for the render-all / element-
      // present path (jsdom), and ADD `scrollToIndex` for the real-layout /
      // off-window path (do NOT replace one with the other). scrollIntoView is
      // unimplemented in jsdom and absent on very old engines — guard so the
      // focus never throws.
      const row =
        typeof document !== 'undefined'
          ? document.querySelector(
              `[data-testid="triage-ledger-row-${attrValueEscape(focusId)}"]`,
            )
          : null;
      if (row && typeof (row as HTMLElement).scrollIntoView === 'function') {
        // Element is mounted (render-all, or already inside the window) — scroll
        // it into view directly. This keeps the existing focus tests green.
        (row as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Element is off-window (real layout) — resolve its index in the freshly
        // built flat list and scroll the virtualizer to it, never a memoized
        // index (§ 2).
        const i = renderRows.findIndex(
          (r) => r.type === 'item' && r.item.id === focusId,
        );
        if (i >= 0) virtualizer.scrollToIndex(i, { align: 'center' });
      }
      return;
    }

    // Not on a loaded page yet — auto-page toward it, capped. The
    // `!isFetchingNextPage` guard is shared with the scroll sentinel, so the
    // two fetch triggers serialize and never double-fetch a page.
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      autoPageAttemptsRef.current < MAX_FOCUS_AUTO_PAGES
    ) {
      autoPageAttemptsRef.current += 1;
      fetchNextPage();
      return;
    }

    // Terminal: past the cap, or no more pages — and still not found. Never a
    // silent no-op: surface the hint (the treasurer can keep scrolling to load
    // more). Clear the one-shot (only here, never mid-loop) so that if a later
    // manually-scrolled page DOES contain the row, this effect re-fires and
    // focuses it; a fresh navigation re-runs the reset effect above. Don't
    // declare a miss while a fetch is still settling.
    if (!isFetchingNextPage) {
      setFocusMissed(true);
      focusedRef.current = null;
    }
  }, [
    focusId,
    items,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    owedWindow,
    laterExpanded,
    renderRows,
    virtualizer,
    scrollEl,
  ]);

  // The deep-link highlight ring clears itself 2 s after it is set — decoupled
  // from the focus effect so its re-runs can't cancel the pending clear.
  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), 2000);
    return () => clearTimeout(timer);
  }, [highlightId]);

  // Row-memory mark after closing details. Shorter than the deep-link focus ring:
  // enough for the eye to catch the parent row after layout shifts, not long
  // enough to read as a persistent warning.
  useEffect(() => {
    if (!recentlyCollapsedId) return;
    const timer = setTimeout(() => setRecentlyCollapsedId(null), 1400);
    return () => clearTimeout(timer);
  }, [recentlyCollapsedId]);

  const focusLedgerRow = useCallback((itemId: string) => {
    const row = document.querySelector(
      `[data-testid="triage-ledger-row-${attrValueEscape(itemId)}"]`,
    );
    if (!(row instanceof HTMLElement)) return;
    row.focus({ preventScroll: true });
    if (typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  useEffect(() => {
    if (!focusAfterCollapseId) return;
    focusLedgerRow(focusAfterCollapseId);
    setFocusAfterCollapseId(null);
  }, [focusAfterCollapseId, focusLedgerRow]);

  // Stable identity (empty deps) — reads the current expanded id off the ref, not
  // a captured `expandedId`, so a sibling expand/collapse never re-creates this
  // callback and every row it's passed to can bail out of React.memo (root cause
  // #2). setState updaters are React-stable, so no other deps are needed.
  const toggleExpandedRow = useCallback((itemId: string) => {
    const current = expandedIdRef.current;
    if (current === itemId) {
      setRecentlyCollapsedId(itemId);
      setFocusAfterCollapseId(itemId);
      setExpandedId(null);
      return;
    }

    if (current) setRecentlyCollapsedId(current);
    setExpandedId(itemId);
  }, []);

  const collapseExpandedRow = useCallback((itemId?: string) => {
    const collapsedId = expandedIdRef.current ?? itemId ?? null;
    if (collapsedId) {
      setRecentlyCollapsedId(collapsedId);
      setFocusAfterCollapseId(collapsedId);
    }
    setExpandedId(null);
  }, []);

  // Infinite-scroll: fetch the next page when the bottom sentinel enters the
  // viewport, guarded so it never fires while a fetch is in flight or when no
  // pages remain. Append-only — no upward fetch, no page eviction.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (
        entries.some((e) => e.isIntersecting) &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
    // `hasRows` re-runs the effect when the sentinel first mounts (data arrives
    // after a null loading render); the guards re-bind on fetch-state changes.
  }, [hasRows, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Cold initial load — no cached data yet AND no active filter: show a ledger
  // skeleton so the panel reads as loading instead of blank (the top campaign
  // spinner alone left this area empty). Deliberately gated on `!filterActive`:
  // a post-filter loading flash keeps the bail-to-null below, so narrowing a
  // filter never flashes a skeleton over the results the treasurer is refining.
  if (isLoading && !filterActive) return <TriageLedgerSkeleton />;

  // The ledger read FAILED. Returning null here (the old behaviour) rendered a blank panel,
  // and sitting beside the "you're all caught up" card it told the treasurer there was nothing
  // to do while the server was erroring — the most dangerous shape a finance screen can take.
  // Say so plainly instead; the retry is a reload, which the treasurer already knows how to do.
  if (isError && !filterActive) {
    return (
      <div
        role="alert"
        data-testid="triage-ledger-error"
        className="mt-4 rounded-md border border-destructive bg-destructive/5 px-3 py-2 text-sm text-destructive"
      >
        We couldn't load your transactions just now. Nothing has been lost — refresh the page to
        try again.
      </div>
    );
  }

  // ADR-026 (PR-D) — bail in every non-render case. Because `filteredEmpty`
  // already carries !isLoading && !isError && !!data, this returns null whenever
  // `filterActive && (isLoading || isError || !data)` (the post-filter loading
  // flash) and falls through ONLY for `hasRows` or the precise `filteredEmpty`
  // predicate — so the return-scope `firstPage` read below never throws.
  if (!hasRows && !filteredEmpty) return null;

  // Past the combined guard, `data` is defined (both fall-through cases require
  // !!data). This non-optional page-0 read (distinct from the module-scope
  // OPTIONAL `first`, which the pre-guard renderRows memo needs on the loading
  // render) backs the "Showing N of M" matchedTotal (period-scoped, identical on
  // every page).
  const firstPage = data!.pages[0];

  // While actively paging toward a deep-linked row, show a light hint (not the
  // missed-terminal hint, which only appears once the capped search gives up).
  const isFindingFocusRow =
    !!focusId && !focusMissed && !items.some((it) => it.id === focusId);

  // Renders one `RenderRow` — a header `<tr>` (carrying its testid) or the merged
  // single-`<tr>` data row. `measureRef`/`dataIndex` are wired only on the
  // windowed path so the virtualizer learns each row's real height.
  const renderLedgerRow = (
    row: RenderRow,
    measureRef?: (node: Element | null) => void,
    dataIndex?: number,
  ): ReactNode => {
    if (row.type === 'header') {
      return (
        <TableRow
          ref={measureRef}
          data-index={dataIndex}
          data-testid={row.testid}
          className={row.rowClassName}
        >
          <TableCell colSpan={7} className={row.cellClassName}>
            {row.render()}
          </TableCell>
        </TableRow>
      );
    }
    // Duplicate flag for a contact-bearing row — a Contribution (keyed on the
    // contributor) OR an Expenditure (keyed on the payee). Other kinds carry no
    // contact and never match. Drives the collapsed badge + the expanded resolve
    // panel for both donor and vendor duplicates.
    const rowContactId = rowDuplicateContactId(row.item);
    const duplicateFlag = rowContactId
      ? flagByContactId.get(rowContactId)
      : undefined;
    const hasOrgTypeConflict = rowContactId
      ? orgTypeConflictContactIds.has(rowContactId)
      : false;
    const purposeFlagged = flaggedPurposeIds.has(row.item.id);
    return (
      <TriageLedgerRow
        item={row.item}
        expanded={expandedId === row.item.id}
        highlighted={highlightId === row.item.id}
        recentlyCollapsed={recentlyCollapsedId === row.item.id}
        justCompleted={justCompletedId === row.item.id}
        duplicateFlag={duplicateFlag}
        hasOrgTypeConflict={hasOrgTypeConflict}
        purposeFlagged={purposeFlagged}
        onToggle={toggleExpandedRow}
        onCollapse={collapseExpandedRow}
        onClassifiedFollowUp={handleClassifyFollowUp}
        periodNote={row.periodNote}
        rowRef={measureRef}
        dataIndex={dataIndex}
      />
    );
  };

  return (
    <div
      ref={listContainerRef}
      data-testid="triage-ledger"
      // While a filter is active, reserve ~a screenful so narrowing the results
      // (down to a single match) doesn't collapse the list height and snap the
      // page. Only when filtering — the unfiltered view keeps its natural height.
      className={cn('mt-4', filterActive && 'min-h-[60vh]')}
    >
      {/* A deep link to a row in deep history pages toward it (capped). Once the
          capped search gives up, land at the top with this non-silent hint —
          never a silent no-op (ADR-026). */}
      {focusMissed && (
        <p
          data-testid="triage-ledger-focus-miss-hint"
          className="mb-2 rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground"
        >
          This transaction is in an earlier period — scroll down to load more.
        </p>
      )}
      {/* Light progress affordance while the deep-link loop pages toward the
          target row. */}
      {isFindingFocusRow && (
        <p
          data-testid="triage-ledger-focus-searching"
          className="mb-2 text-sm text-muted-foreground"
        >
          Finding this transaction…
        </p>
      )}
      {/* Treasurer-first, low-ceremony instruction — renders once, above the
          table. The leading checkbox is the readiness gesture it describes. */}
      <p
        data-testid="triage-ledger-subline"
        className="mb-2 text-sm text-muted-foreground"
      >
        Check a row when it&apos;s ready to report — uncheck to reopen before
        it&apos;s filed.
      </p>
      {/* ADR-026 (PR-D) — the display filter bar: a Type multi-toggle + a
          debounced payee/contributor search. Narrows only the display query; the
          NeedsAction work queue + the File-gate counts stay unfiltered. */}
      <div
        data-testid="triage-ledger-filter-bar"
        className="mb-3 flex flex-wrap items-center gap-3"
      >
        {/* Single-select (`type="single"`) — one Type at a time. Radix hands back
            the chosen value (or `''` when the active chip is toggled off);
            every value is a hard-coded entry from FILTER_KIND_ORDER, so the cast
            is safe. */}
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          data-testid="triage-ledger-filter-type"
          aria-label="Filter by type"
          value={selectedKind}
          onValueChange={(v) => setSelectedKind(v as TriageItem['kind'] | '')}
        >
          {FILTER_KIND_ORDER.map((kind) => (
            <ToggleGroupItem
              key={kind}
              value={kind}
              data-testid={`triage-ledger-filter-type-${kind}`}
            >
              {kindLabel(kind)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Input
          type="text"
          data-testid="triage-ledger-filter-search"
          aria-label="Search payee or contributor"
          placeholder="Search payee or contributor"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="h-8 w-56"
        />
        {/* "Showing N of M" — only while a filter is active AND there are matches
            (suppressed at matchedTotal === 0, next to the empty-match message).
            Separate node from any period-count line; reads the page-0 scalar. */}
        {filterActive && firstPage.matchedTotal > 0 && (
          <span
            data-testid="triage-ledger-showing-count"
            className="text-sm text-muted-foreground"
          >
            Showing {items.length} of {firstPage.matchedTotal}
          </span>
        )}
        <ClassificationHintSummary items={items} />
      </div>
      {filteredEmpty ? (
        // Filter active + zero matches: keep the bar reachable, offer a clear
        // path. The <Table>/virtualizer stay out of this branch (nothing to
        // window).
        <div className="py-10 text-center">
          <p
            data-testid="triage-ledger-empty-match"
            className="text-sm text-muted-foreground"
          >
            No transactions match this filter.
          </p>
          <Button
            variant="link"
            data-testid="triage-ledger-clear-filters"
            onClick={() => {
              setSelectedKind('');
              setSearchInput('');
              setDebouncedQuery('');
            }}
          >
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                {/* The header is ONE `colSpan={7}` cell whose inner grid uses the
                    SAME LEDGER_ROW_GRID_TEMPLATE as every data row, so the labels
                    sit exactly above their columns. Two independent grids can only
                    align when the template has no content-based `auto` tracks — see
                    the constant's note. Mirrors the body cell: `p-0` on the cell,
                    `p-2` on the inner grid, so the left edges line up. */}
                <TableHead colSpan={7} className="h-auto p-0">
                  <div
                    className="grid items-center gap-x-2 p-2"
                    style={{ gridTemplateColumns: LEDGER_ROW_GRID_TEMPLATE }}
                  >
                    <span aria-hidden />
                    <span>Date</span>
                    <span>Payee / contributor</span>
                    <span>Category</span>
                    <span className="text-right">Amount</span>
                    <span aria-hidden />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderAll
                ? // jsdom / not-yet-laid-out: render EVERY RenderRow in order, no
                  // spacers. One `<tr>` per item/header keeps the ordering + all
                  // testids exactly as before virtualization (§ jsdom degradation).
                  renderRows.map((row) => (
                    <Fragment key={row.key}>{renderLedgerRow(row)}</Fragment>
                  ))
                : // Real layout: window over the flat list, bracketed by two
                  // layout-only spacer `<tr>`s that reserve the off-window scroll
                  // extent without mounting off-window rows (§ 3).
                  (() => {
                    const virtualItems = virtualizer.getVirtualItems();
                    const totalSize = virtualizer.getTotalSize();
                    const topPad = virtualItems.length
                      ? virtualItems[0].start - virtualizer.options.scrollMargin
                      : 0;
                    const bottomPad = virtualItems.length
                      ? totalSize -
                        (virtualItems[virtualItems.length - 1].end -
                          virtualizer.options.scrollMargin)
                      : totalSize;
                    return (
                      <>
                        {topPad > 0 && (
                          <tr aria-hidden>
                            <td colSpan={7} style={{ height: topPad }} />
                          </tr>
                        )}
                        {virtualItems.map((v) => {
                          const row = renderRows[v.index];
                          if (!row) return null;
                          return (
                            <Fragment key={row.key}>
                              {renderLedgerRow(row, virtualizer.measureElement, v.index)}
                            </Fragment>
                          );
                        })}
                        {bottomPad > 0 && (
                          <tr aria-hidden>
                            <td colSpan={7} style={{ height: bottomPad }} />
                          </tr>
                        )}
                      </>
                    );
                  })()}
            </TableBody>
          </Table>
          {/* Infinite-scroll sentinel: fetches the next keyset page when it scrolls
              into view (ADR-026 PR-B). Zero-height, purely a fetch trigger. */}
          <div
            ref={sentinelRef}
            data-testid="triage-ledger-scroll-sentinel"
            aria-hidden
            className="h-px"
          />
          {isFetchingNextPage && (
            <p
              data-testid="triage-ledger-loading-more"
              className="py-2 text-center text-sm text-muted-foreground"
            >
              Loading more…
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Escapes a row id for safe interpolation into a double-quoted
 * `[data-testid="…"]` attribute selector. Entity ids are GUIDs in production
 * (no special chars), so this is purely defensive: escape the only two chars
 * (`"` and `\`) that could break out of the quoted attribute value.
 */
function attrValueEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

/**
 * A money row a filing stamped Reported. It derives to display `status: 'Done'` once an
 * amendment reopens its period (ADR-019 D6), yet it is on file: unchecking it is refused by
 * the server (NotConfirmed), and a change to it goes on the amendment.
 */
function isMoneyRowStampedReported(item: TriageItem): boolean {
  if (item.kind === 'Expenditure') return item.expenditure.ledgerStatus === 'Reported';
  if (item.kind === 'Contribution') return item.contribution.ledgerStatus === 'Reported';
  return false;
}

const REOPENED_PERIOD_REPORTED_NOTE =
  'Already on a report you filed. This period is open again, so a change to it goes on the amendment.';

/**
 * Delete is the one detail action a refund or bank line shares with the money rows. Those two
 * kinds carry no `freeze`, so only a Reported status hides their delete.
 */
function canDeleteRow(item: TriageItem): boolean {
  if (item.kind === 'Contribution' || item.kind === 'Expenditure') {
    return moneyRowAffordances(item.freeze).delete;
  }
  return item.status !== 'Reported';
}

/**
 * Treasurer words at the render boundary — never enum names. Names the ENTITY kind (the row
 * is whatever the transaction turned out to be).
 */
function kindLabel(kind: TriageItem['kind']): string {
  switch (kind) {
    case 'BankLine':
      return 'Bank transaction';
    case 'Contribution':
      return 'Contribution';
    case 'Expenditure':
      return 'Expenditure';
    case 'Refund':
      return 'Refund';
    default:
      return assertNever(kind);
  }
}

/**
 * Flag text for an amber (not-yet-checkable) row — treasurer copy at the
 * render boundary, reusing the existing `missingFieldsLabel` helper. Empty for
 * non-flagged actions (the caller only renders it for flagged rows).
 */
function flagLabel(item: TriageItem): string {
  switch (item.actionNeeded) {
    case 'Classify':
      // A freshly-imported bank line that hasn't been recorded yet — the
      // treasurer reviews it and records it as a contribution / expense /
      // transfer (or dismisses it).
      return 'Needs review';
    case 'ProvideDonorInfo':
      return `missing ${missingFieldsLabel(
        item.kind === 'Contribution' ? item.missingFields ?? [] : [],
      )}`;
    case 'ProvideExpenditureInfo':
      return 'Needs payee address';
    case 'CategorizeExpenditure':
      return 'Needs a category';
    case 'MatchRefund':
      return 'Match to original';
    case 'ResolveContributorShape':
      return 'Person or organization?';
    case 'ClassifyOrganization':
      return 'Needs an organization type';
    case 'Confirm':
    case null:
    default:
      return '';
  }
}

/**
 * The fuller, sentence-form guidance shown in the expansion banner — what the
 * short flag label means, so an opened row still tells the treasurer exactly
 * what's needed. Empty for non-flagged rows.
 */
function flagBannerText(item: TriageItem, stateDisplayName: string | null): string {
  // Jurisdiction-generic banners must not hardcode a state name — the flag can fire on
  // any jurisdiction whose gate is (or becomes) active, and asserting the wrong state on
  // a filing-blocking prompt is a leak. Thread the served display name; when it is absent
  // (federal, or profile not loaded) drop to the neutral "the file" phrasing.
  const jurisdiction = stateDisplayName ?? 'the state';
  switch (item.actionNeeded) {
    case 'Classify':
      return 'Review this transaction and record it below — a contribution, an expense, or a transfer.';
    case 'ProvideDonorInfo':
      return `Missing ${missingFieldsLabel(
        item.kind === 'Contribution' ? item.missingFields ?? [] : [],
      )} for this donor.`;
    case 'ProvideExpenditureInfo':
      return 'This payee needs a full address before the expenditure can be itemized.';
    case 'CategorizeExpenditure':
      return 'Choose a category for this expenditure — an uncategorized expenditure can’t be filed.';
    case 'MatchRefund':
      return 'Match this refund to its original contribution, or mark it a standalone refund.';
    case 'ResolveContributorShape':
      return "This gift's type and the donor's record disagree on whether the donor is a person or an organization. Set the donor to match so the file is accepted.";
    case 'ClassifyOrganization':
      return `This organization donor needs a type (PAC, party, corporation, …) before ${jurisdiction} will accept the file. Pick one below — it’s saved on the donor.`;
    case 'Confirm':
    case null:
    default:
      return '';
  }
}

/**
 * Lightweight CSS hover tooltip for the symbols-only status column — no
 * library, no JS. Appears instantly on hover (the native `title` tooltip has a
 * fixed ~700ms browser delay that can't be tuned). The label is `aria-hidden`
 * here because the wrapped symbol already carries it on `aria-label`, and it
 * sits OUTSIDE the symbol element so the symbol's own `textContent` stays empty
 * (the status column shows no visible words).
 */
function StatusTooltip({
  label,
  detail,
  children,
}: {
  label: string;
  /**
   * §34 — an optional second line under the label, used for the portal-upload
   * filing-model boundary ("not yet sent to the portal" / "on file only after
   * you upload"). When present the tooltip widens and wraps so the sentence is
   * never truncated, honoring accessibility (large/bold text must wrap).
   */
  detail?: string;
  children: ReactNode;
}) {
  // Positioned to the RIGHT of the symbol (not below/centered): the status
  // column is the leftmost, so a centered tooltip is clipped on the left by the
  // shadcn Table's `overflow-auto` wrapper. Sitting to the right keeps it inside
  // the table box and clear of the top/bottom row edges.
  return (
    <span className="group relative inline-flex">
      {children}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute left-full top-1/2 z-30 ml-2 -translate-y-1/2 rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-md transition-opacity duration-75 group-hover:opacity-100',
          detail ? 'w-64 whitespace-normal' : 'whitespace-nowrap',
        )}
      >
        <span className="font-medium">{label}</span>
        {detail && (
          <span className="mt-1 block font-normal leading-snug text-gray-200">{detail}</span>
        )}
      </span>
    </span>
  );
}

function parseAmountDraft(draft: string): number {
  const parsed = Number(draft);
  if (!Number.isFinite(parsed)) {
    throw new Error('Enter a valid amount.');
  }
  return Math.round(parsed * 100) / 100;
}

/**
 * Description cell — DISPLAY-ONLY in the dense register (ADR-016, 2026-06-25).
 * Per-field editing (bank description, expenditure payee/purpose, refund reason)
 * moved into the expanded row detail (`detailFields`). The contribution
 * contributor stays a navigation Link here — that is navigation, not an edit,
 * and `isInteractiveTarget` keeps a click on it from toggling the row.
 */
function DescriptionCell({ item }: { item: TriageItem }) {
  switch (item.kind) {
    case 'BankLine':
      // The lingering-duplicate hint moved to the shared BankLineInfoHeader (#591);
      // the deposit hint stays here — it's a NeedsAction-credit reconciliation nudge,
      // not one of the three cross-surface line-context signals.
      return (
        <div className="flex flex-col gap-1">
          <span className="block truncate">{item.bt.description}</span>
          {item.bt.depositHint && (
            <DepositHintChip
              id={item.id}
              amount={item.bt.amount}
              hint={item.bt.depositHint}
            />
          )}
        </div>
      );
    case 'Contribution': {
      const showMissing =
        item.actionNeeded === 'ProvideDonorInfo' &&
        item.missingFields != null &&
        item.missingFields.length > 0;
      const missingText = missingFieldsLabel(item.missingFields ?? []);
      return (
        <span className="block truncate">
          <Link
            to="/admin/finance/contacts/$contactId"
            params={{ contactId: item.contribution.contactId }}
            className="hover:underline underline-offset-2"
          >
            {item.contribution.contactName}
          </Link>
          {showMissing && (
            <>
              {' — missing '}
              {missingText}
            </>
          )}
        </span>
      );
    }
    case 'Expenditure': {
      const { expenditure } = item;
      return (
        <span className="block truncate">
          {expenditure.payeeName}
          {expenditure.purpose && (
            <span className="text-muted-foreground">
              {' — '}
              {expenditure.purpose}
            </span>
          )}
        </span>
      );
    }
    case 'Refund':
      return (
        <span className="block truncate">{item.refund.reason || '—'}</span>
      );
    default:
      return assertNever(item);
  }
}

/**
 * The treasurer-SELECTABLE expenditure categories. `Uncategorized` is the import
 * placeholder — a system "not yet chosen" state, never a choice the treasurer picks —
 * so it's excluded here. LoanRepayment and OtherDisbursement come only from the federal
 * prior-filing import today and file on their own FEC summary lines, so re-picking them
 * here would move a row between summary lines; they're excluded too. A row already carrying any of these displays it as a disabled current
 * value in the editor.
 */
const NON_SELECTABLE_EXPENDITURE_CATEGORIES: readonly ExpenditureCategory[] = [
  ExpenditureCategory.Uncategorized,
  ExpenditureCategory.LoanRepayment,
  ExpenditureCategory.OtherDisbursement,
];
// Annotated as the full union so `.includes(expenditure.category)` below can test a
// possibly-non-selectable value against the selectable set.
const EXPENDITURE_CATEGORY_OPTIONS: ExpenditureCategory[] = Object.values(
  ExpenditureCategory,
).filter((c) => !NON_SELECTABLE_EXPENDITURE_CATEGORIES.includes(c));

type ExpenditureSlim = Extract<TriageItem, { kind: 'Expenditure' }>['expenditure'];
type UpdateExpenditureFn = ReturnType<
  typeof useUpdateExpenditure
>['mutateAsync'];

/**
 * The trailing icon on an inline-edit trigger: a spinner while the field's save is in
 * flight (every save is an HTTP round-trip), the edit pencil otherwise. One home for
 * the shared size / animation / aria so every inline-edit affordance stays consistent.
 * The caller supplies the spinner's `data-testid` (each field's is distinct).
 */
function InlineEditIcon({
  saving,
  savingTestId,
}: {
  saving: boolean;
  savingTestId: string;
}) {
  return saving ? (
    <InlineSpinner testId={savingTestId} label="Saving" className="h-3 w-3" />
  ) : (
    <Pencil className="h-3 w-3 opacity-60" aria-hidden="true" />
  );
}

/**
 * Reassign an expenditure's payee to a different contact; an echo of the current payee
 * is a no-op. Shared by the normal payee editor and the needs-address
 * body (which surface the mapped error in different places, so the caller passes its
 * own error setter). Returns `isReassigning` to drive the inline spinner.
 */
function useReassignPayee(
  expenditure: ExpenditureSlim,
  onError: (message: string | null) => void,
) {
  const { mutateAsync: updateExpenditure } = useUpdateExpenditure();
  const [isReassigning, setIsReassigning] = useState(false);

  const reassign = useCallback(
    async (contactId: string) => {
      if (contactId === expenditure.payeeContactId) return;
      onError(null);
      setIsReassigning(true);
      try {
        await updateExpenditure({ row: expenditure, payeeContactId: contactId });
      } catch (err) {
        onError(expenditureEditErrorMessage(err));
      } finally {
        setIsReassigning(false);
      }
    },
    [
      updateExpenditure,
      onError,
      expenditure,
    ],
  );

  return { reassign, isReassigning };
}

/**
 * The editable Expenditure payee/purpose detail field (relocated out of the
 * register into the expanded row detail, ADR-016): payee (click-to-open vendor
 * picker) — purpose (inline edit). (The category re-pick now lives in the
 * expanded row detail too — `ExpenditureCategoryField` / `ExpenditureCategoryEdit`.)
 */
function ExpenditurePayeePurposeEdit({
  id,
  expenditure,
  updateExpenditure,
}: {
  id: string;
  expenditure: ExpenditureSlim;
  updateExpenditure: UpdateExpenditureFn;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pickingVendor, setPickingVendor] = useState(false);
  // Reassigning the payee is an HTTP round-trip; `isReassigning` drives a spinner in
  // place of the pencil so the treasurer sees the click registered (the picker closes
  // on select). Shared with the needs-address body via `useReassignPayee`.
  const { reassign, isReassigning } = useReassignPayee(expenditure, setError);

  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1 truncate">
        {pickingVendor ? (
          <ExpenditureVendorPicker
            id={id}
            onSelect={(contactId) => {
              setPickingVendor(false);
              void reassign(contactId);
            }}
            onCancel={() => setPickingVendor(false)}
          />
        ) : (
          <button
            type="button"
            data-testid={`triage-ledger-payee-button-${id}`}
            className={cn(financeInlineEditTriggerClass, 'shrink-0 cursor-pointer')}
            aria-label="Edit payee"
            title="Edit payee"
            disabled={isReassigning}
            onClick={() => setPickingVendor(true)}
          >
            {expenditure.payeeName}
            <InlineEditIcon
              saving={isReassigning}
              savingTestId={`triage-ledger-payee-saving-${id}`}
            />
          </button>
        )}
        <span className="text-muted-foreground">—</span>
        <InlineEditCell
          id={id}
          field="desc"
          editLabel="purpose"
          value={expenditure.purpose}
          display={expenditure.purpose || 'Add purpose'}
          onSave={async (next) => {
            await updateExpenditure({ row: expenditure, purpose: next });
          }}
        />
      </span>
      {error && (
        <p
          role="alert"
          data-testid={`triage-ledger-payee-error-${id}`}
          className="text-xs text-rose-700"
        >
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The Category column — the unified CLASSIFICATION slot (2026-07-20), DISPLAY-ONLY
 * in the dense register (ADR-016). One column that reads coherently across the
 * mixed row kinds: the expense category for money-out (Expenditure), the
 * contributor designation for money-in (Contribution), an em-dash for rows that
 * have neither (BankLine / Refund). The re-pickable category dropdown + the
 * designation editor both live in the expanded row detail.
 *
 * For a Contribution the slot lets the treasurer verify contributor type at a
 * glance and makes a suspected-miscategorized PAC jump out (`ContributionClassificationCell`).
 */
/**
 * The ledger's amber attention idiom, as one component: the circular `!` glyph plus a
 * TEXT label (never colour alone, and truncating so it stays legible at large-text
 * accessibility scaling) in the same amber the flagged-row wash and FlagBanner use.
 *
 * Extracted because three rows now speak it — "Looks like a PAC", "Maybe a person —
 * verify." and the disbursement-purpose nudge. Three hand-copied class strings drift
 * into three slightly different ambers the first time anyone tunes one.
 */
function AmberAdvisoryPill({
  testId,
  title,
  label,
}: {
  testId: string;
  title: string;
  label: string;
}) {
  return (
    <span
      data-testid={testId}
      title={title}
      className="inline-flex min-w-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
    >
      <span
        aria-hidden="true"
        className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-amber-500 text-[9px] font-bold leading-none text-amber-600"
      >
        !
      </span>
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * "4 hints ready · 2 need a closer look · none applied until you accept" — the classification
 * hints across the loaded rows, split by whether they need a closer look. Hidden without hints.
 */
function ClassificationHintSummary({ items }: { items: TriageItem[] }) {
  const hints = items.flatMap((it) =>
    it.kind === 'BankLine' && it.bt.classificationHint ? [it.bt.classificationHint] : [],
  );
  if (hints.length === 0) return null;
  const closerLook = hints.filter((h) => h.caution).length;
  const ready = hints.length - closerLook;
  return (
    <span
      data-testid="triage-ledger-hint-summary"
      className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground"
    >
      <span aria-hidden="true" className="h-[7px] w-[7px] shrink-0 rounded-full bg-primary" />
      {ready} {ready === 1 ? 'hint' : 'hints'} ready · {closerLook}{' '}
      {closerLook === 1 ? 'needs' : 'need'} a closer look · none applied until you accept
    </span>
  );
}

/** The "from hint" / "from hint · edited" tag on a row classified from a classification hint. */
function HintProvenanceTag({
  id,
  provenance,
}: {
  id: string;
  provenance: ExpenditureHintProvenance;
}) {
  let label: string;
  switch (provenance) {
    case ExpenditureHintProvenance.Manual:
      return null;
    case ExpenditureHintProvenance.FromHint:
      label = 'from hint';
      break;
    case ExpenditureHintProvenance.FromHintEdited:
      label = 'from hint · edited';
      break;
    default:
      return assertNever(provenance);
  }
  return (
    <span
      data-testid={`triage-ledger-hint-provenance-${id}`}
      title="Recorded in the audit log"
      className="shrink-0 rounded-full border border-border px-1.5 text-[0.625rem] text-muted-foreground"
    >
      {label}
    </span>
  );
}

/**
 * One-click Accept on a classification hint: the same act as Classify, sent with exactly the
 * hint's values. On success the row refetches as a classified expenditure.
 */
function AcceptHintButton({
  bt,
  hint,
}: {
  bt: SortInboxBankTransactionDto;
  hint: BankTransactionClassificationHintDto;
}) {
  const { mutate: classify, isPending, error } = useClassifyBankTransactionAsExpenditure({
    scopeId: bt.id,
  });
  const setLastAccepted = useLastAcceptedHintStore((state) => state.setLastAccepted);
  return (
    <span className="flex shrink-0 flex-col items-end gap-0.5">
      <PendingButton
        variant="outline"
        size="sm"
        pending={isPending}
        onClick={() =>
          classify(
            { id: bt.id, data: acceptHintRequest(bt.concurrencyVersion, hint) },
            { onSuccess: (created) => setLastAccepted(created.id) },
          )
        }
      >
        Accept
      </PendingButton>
      {error ? (
        <span role="alert" className="text-[11px] text-destructive">
          {error.message}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Undo on the most recent one-click Accept: the expenditure Delete without its warning dialog.
 * The bank line is never deleted; it returns unattributed and shows its hint again on refetch.
 */
function UndoAcceptButton({
  item,
  onRefused,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
  onRefused: (refusal: DeleteRefusal) => void;
}) {
  const { mutate: deleteExpenditure, isPending } = useDeleteExpenditure();
  const clearLastAccepted = useLastAcceptedHintStore((state) => state.clearLastAccepted);
  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() =>
        deleteExpenditure(
          {
            row: item.expenditure,
          },
          {
            onSuccess: clearLastAccepted,
            onError: (err) => onRefused(detailDeleteRefusal(err)),
          },
        )
      }
      className="shrink-0 text-[11px] text-foreground underline decoration-primary underline-offset-[3px] disabled:opacity-50"
    >
      Undo
    </button>
  );
}

function ExpenditureCategoryCell({
  item,
  purposeFlagged,
  onReviewHint,
}: {
  item: TriageItem;
  purposeFlagged?: boolean;
  /** Opens the row so a cautioned classification hint can be reviewed in the classify form. */
  onReviewHint?: () => void;
}) {
  // Hooks before the early return so the ordering is stable across renders.
  const { data: settings } = useFinanceSettings();
  const lastAccepted = useIsLastAcceptedHint(item);
  // A refused Undo can never succeed on retry (the row is filed or locked), so the reason
  // replaces the Undo and sits on its own line under the category, where it has room to wrap.
  const [undoRefusal, setUndoRefusal] = useState<DeleteRefusal | null>(null);
  if (item.kind === 'Contribution') {
    return <ContributionClassificationCell item={item} />;
  }
  // A money-out row's expense category now reads as a chip, matching the slate
  // pill idiom the contribution designations already use — so the Category column
  // is chips across the board, not chips on contributions and bare text on
  // expenditures.
  if (item.kind === 'Expenditure') {
    return (
      <span
        data-testid={`triage-ledger-category-${item.id}`}
        className="flex min-w-0 flex-col items-start gap-0.5"
      >
        <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-muted-foreground">
          <span
            data-testid={`triage-ledger-category-chip-${item.id}`}
            className="inline-flex min-w-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            <span className="truncate">
              {expenditureCategoryLabel(item.expenditure.category, settings?.rulesetCode)}
            </span>
          </span>
          <HintProvenanceTag
            id={item.id}
            provenance={item.expenditure.hintProvenance}
          />
          {lastAccepted && !undoRefusal && moneyRowAffordances(item.freeze).delete ? (
            <UndoAcceptButton item={item} onRefused={setUndoRefusal} />
          ) : null}
          {purposeFlagged ? (
            // The disbursement-purpose nudge, moved off the review-and-file modal and onto the
            // row it was typed on. The flag comes from the preflight verdict (threaded down as a
            // boolean), so a row is marked on exactly the condition that will raise the warning
            // at filing — the sub-threshold rows it deliberately no longer raises stay unmarked.
            <AmberAdvisoryPill
              testId={`triage-ledger-purpose-advisory-${item.id}`}
              title={PreflightCopy.FederalDisbursementPurposeMissingLedgerTooltip}
              label={PreflightCopy.FederalDisbursementPurposeMissingLedgerLabel}
            />
          ) : null}
        </span>
        {undoRefusal ? (
          <span
            role="alert"
            className="flex max-w-full flex-col items-start whitespace-normal break-words text-[11px] text-destructive"
          >
            {undoRefusal.message}
            {undoRefusal.filedReport && (
              <FiledReportAmendLink
                filedReport={undoRefusal.filedReport}
                testId={`triage-ledger-undo-filed-report-link-${item.id}`}
              />
            )}
          </span>
        ) : null}
      </span>
    );
  }
  // An unclassified money-out line whose payee the campaign has classified before shows that
  // past classification, faded, where the em-dash would be. It stays a hint: the row is still
  // unclassified until the treasurer classifies it.
  if (item.kind === 'BankLine' && item.bt.classificationHint?.caution) {
    // A hint that needs a closer look: lighter, no dot or underline, the caution in an amber
    // pill, and Review only — never one-click Accept.
    const hint = item.bt.classificationHint;
    return (
      <span
        data-testid={`triage-ledger-category-${item.id}`}
        className="flex min-w-0 items-start gap-2"
      >
        <span className="flex min-w-0 flex-col items-start gap-0.5">
          <span
            data-testid={`triage-ledger-classification-hint-${item.id}`}
            className="max-w-full whitespace-normal break-words text-[0.8125rem] text-slate-400"
          >
            {hint.payeeName} · {expenditureCategoryLabel(hint.category, settings?.rulesetCode)} ·
            purpose needed
          </span>
          <span
            data-testid={`triage-ledger-classification-hint-caution-${item.id}`}
            className="max-w-full whitespace-normal break-words rounded-md bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
          >
            {hint.cautionReason}
          </span>
        </span>
        <Button variant="outline" size="sm" className="shrink-0" onClick={onReviewHint}>
          Review
        </Button>
      </span>
    );
  }
  if (item.kind === 'BankLine' && item.bt.classificationHint) {
    const hint = item.bt.classificationHint;
    return (
      <span
        data-testid={`triage-ledger-category-${item.id}`}
        className="flex min-w-0 items-start gap-2"
      >
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-start gap-1.5 text-[0.8125rem] text-slate-500">
            <span
              aria-hidden="true"
              className="mt-[0.4em] h-[7px] w-[7px] shrink-0 rounded-full border-[1.5px] border-primary bg-primary"
            />
            <span
              data-testid={`triage-ledger-classification-hint-${item.id}`}
              className="min-w-0 whitespace-normal break-words underline decoration-slate-400 decoration-dashed underline-offset-[3px]"
            >
              {hint.payeeName} · {expenditureCategoryLabel(hint.category, settings?.rulesetCode)} ·{' '}
              {hint.purpose ? `"${hint.purpose}"` : 'purpose needed'}
            </span>
          </span>
          <span
            data-testid={`triage-ledger-classification-hint-reason-${item.id}`}
            className="whitespace-normal break-words pl-[calc(7px+0.375rem)] text-[0.6875rem] text-muted-foreground"
          >
            {hint.reason}
          </span>
        </span>
        {hint.oneClickAcceptAllowed ? (
          <AcceptHintButton bt={item.bt} hint={hint} />
        ) : (
          <Button variant="outline" size="sm" className="shrink-0" onClick={onReviewHint}>
            Review
          </Button>
        )}
      </span>
    );
  }
  // A BankLine / Refund has no category to classify — keep the quiet em-dash
  // (a pill wrapped around a dash is just noise).
  return (
    <span
      data-testid={`triage-ledger-category-${item.id}`}
      className="text-muted-foreground"
    >
      —
    </span>
  );
}

/**
 * The captured-check-photo hint — a small camera the treasurer sees WITHOUT
 * expanding (expand → enlarge). Preserved alongside the contributor designation
 * so the collapsed row still signals "this gift has an image on file". Renders
 * nothing when no image is attached.
 */
function CheckPhotoHint({ item }: { item: Extract<TriageItem, { kind: 'Contribution' }> }) {
  if (item.checkImage == null) return null;
  return (
    <StatusTooltip label="Check photo attached">
      <Camera
        data-testid={`triage-ledger-check-photo-icon-${item.id}`}
        aria-label="Check photo attached"
        role="img"
        className="inline h-4 w-4 text-muted-foreground"
      />
    </StatusTooltip>
  );
}

/**
 * A contribution's classification, shown on the COLLAPSED row so a treasurer can
 * verify contributor type at a glance:
 *
 *  1. `suggestsNonIndividualType` (an org-named gift still typed Individual) →
 *     a small amber ADVISORY pill reusing the ledger's native amber attention
 *     language (the circular `!` glyph + `amber-*` tokens the flag banner uses).
 *     This is the actionable "looks like a PAC — verify" signal.
 *  1b. else `suggestsMissingLastName` (a single-token organization shell that may
 *     really be a person imported with only a first name) → the sibling amber
 *     advisory "Maybe a person — verify." — switch to a person and add the last
 *     name, or confirm it really is a one-word org.
 *  2. Otherwise the designation from `contributionTypeLabel`:
 *     - `Cash` (a plain monetary gift; and the retired `Individual` string on
 *       historical rows) → quiet muted plain text, NO pill (the common case stays
 *       low-noise so non-plain rows stand out).
 *     - In-Kind / Federal Transfer / Other → a small rounded pill with a subtle
 *       slate background tint so the eye lands on it.
 *
 * The check-photo camera stays reachable in every case (trailing hint).
 */
function ContributionClassificationCell({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
}) {
  const type = item.contribution.contributionType;
  const label = contributionTypeLabel(type);
  // The low-noise common case: a plain monetary gift stays quiet (no pill) so the
  // non-plain designations (In-Kind / Federal Transfer / Other) stand out. ADR-057 (#344)
  // — the person/organization gift members were retired into the identity-less `Cash`
  // receipt; the retired `"Individual"` string is still treated as quiet so historical
  // audit rows persisted before the collapse read the same low-noise way.
  const isPlainGift = type === ReceiptNature.Cash || (type as string) === 'Individual';
  // ADR-057 — the contributor's person-vs-organization identity lives on the contact,
  // not the gift. Lead the cell with it (an "Organization" / "Individual" tag): that's
  // the signal a treasurer actually scans, where "Cash" on every row carried none.
  const isOrganization = item.contribution.contactKind === ContactKind.Organization;

  return (
    <span
      data-testid={`triage-ledger-category-${item.id}`}
      className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground"
    >
      <span
        data-testid={`triage-ledger-contact-kind-${item.id}`}
        className={cn(
          'inline-flex min-w-0 items-center rounded-full px-2 py-0.5 text-xs font-medium',
          // Organizations are rarer and more compliance-relevant, so they pop a touch
          // more (deeper slate) than the individual-donor common case.
          isOrganization ? 'bg-slate-100 text-slate-700' : 'bg-slate-50 text-slate-500',
        )}
      >
        <span className="truncate">
          {isOrganization ? 'Organization' : 'Individual'}
        </span>
      </span>
      {item.contribution.suggestsNonIndividualType ? (
        // The actionable advisory — reuse the ledger's amber attention idiom (the
        // same amber the flagged-row wash + FlagBanner use) so it reads native.
        // Carries a TEXT label (never color-only) and truncates gracefully so it
        // stays legible at large-text accessibility scaling.
        <AmberAdvisoryPill
          testId={`triage-ledger-pac-advisory-${item.id}`}
          title="This gift is typed Individual but the name looks like a PAC — verify the type."
          label="Looks like a PAC"
        />
      ) : item.contribution.suggestsMissingLastName ? (
        // A single-token organization shell that may really be a person imported with
        // only a first name — nudge the treasurer to verify it (switch to a person +
        // add a last name, or confirm it really is a one-word org). Same amber advisory
        // idiom as the PAC nudge; text label (never color-only), truncates at large-text
        // scaling.
        <AmberAdvisoryPill
          testId={`triage-ledger-missing-lastname-advisory-${item.id}`}
          title="Imported as a single-name organization — this may be a person. If so, switch the type and add a last name."
          label="Maybe a person — verify."
        />
      ) : !isPlainGift ? (
        // A non-plain receipt nature (In-Kind / Federal Transfer / Other) — a subtle
        // slate-tinted pill alongside the contact-kind tag. Plain Cash shows nothing
        // here now: the contact-kind tag leads, and "Cash" added no scanning value.
        <span
          data-testid={`triage-ledger-classification-pill-${item.id}`}
          className="inline-flex min-w-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
        >
          <span className="truncate">{label}</span>
        </span>
      ) : null}
      <CheckPhotoHint item={item} />
    </span>
  );
}

/**
 * The collapsed "possible duplicate of {otherName}" badge on a flagged
 * Contribution row. `otherName` is the flag side whose contactId is NOT this
 * row's contributor — i.e. the record this donor collides with. Small,
 * outline-styled, wraps at large text scaling. Expanding the row opens the
 * resolve panel.
 */
/**
 * The contact a row's duplicate flag is keyed on — the contributor on a
 * Contribution row, the payee on an Expenditure row. Every other kind has no
 * contact and never carries a duplicate flag. One definition keeps the flag
 * lookup (in the ledger) and the badge/panel rendering (in the row) from drifting
 * when the next contact-bearing kind is added.
 */
function rowDuplicateContactId(item: TriageItem): string | undefined {
  return item.kind === 'Contribution'
    ? item.contribution.contactId
    : item.kind === 'Expenditure'
      ? item.expenditure.payeeContactId
      : undefined;
}

function DuplicateBadge({
  flag,
  rowContactId,
}: {
  flag: DuplicateContactFlagDto;
  rowContactId: string;
}) {
  const otherSide =
    flag.contactA.contactId === rowContactId ? flag.contactB : flag.contactA;
  return (
    <Badge
      variant="outline"
      data-testid={`triage-ledger-duplicate-badge-${rowContactId}`}
      className="mt-1 max-w-full whitespace-normal border-amber-300 bg-amber-50 text-left font-normal text-amber-800"
    >
      Possible duplicate of {otherSide.displayName}
    </Badge>
  );
}

/**
 * The collapsed "organization type conflict — resolve" badge (#350) on a row whose contact
 * carries an unresolved contact-merge OrganizationType conflict. Unlike the duplicate badge (whose
 * resolve panel lives in the row expansion), this conflict is fixed on the contact's own page, so
 * the badge is a Link there. `stopPropagation` keeps the click from also toggling the row.
 */
function OrgTypeConflictBadge({ rowContactId }: { rowContactId: string }) {
  return (
    <Link
      to="/admin/finance/contacts/$contactId"
      params={{ contactId: rowContactId }}
      onClick={(e) => e.stopPropagation()}
      data-testid={`triage-ledger-org-type-conflict-badge-${rowContactId}`}
      className="mt-1 inline-block max-w-full whitespace-normal rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-left text-xs font-normal text-amber-800 hover:bg-amber-100"
    >
      Organization type conflict — resolve
    </Link>
  );
}

/**
 * The editable category dropdown, relocated from the register Category cell into
 * the expanded row detail (ADR-016, 2026-06-25). Mounted only for a non-Reported
 * expenditure.
 */
function ExpenditureCategoryEdit({
  id,
  expenditure,
  updateExpenditure,
}: {
  id: string;
  expenditure: ExpenditureSlim;
  updateExpenditure: UpdateExpenditureFn;
}) {
  const [categoryError, setCategoryError] = useState<string | null>(null);
  // A category change is an HTTP round-trip; a spinner next to the dropdown tells
  // the treasurer the pick registered instead of leaving them wondering.
  const [saving, setSaving] = useState(false);
  // AL relabels Transfer / Reimbursement (both file as "Other") — keep the ledger
  // dropdown consistent with the classify picker for the same expenditure.
  const { data: settings } = useFinanceSettings();

  const handleCategoryChange = async (next: ExpenditureCategory) => {
    if (next === expenditure.category) return;
    setCategoryError(null);
    setSaving(true);
    try {
      await updateExpenditure({ row: expenditure, category: next });
    } catch (err) {
      setCategoryError(expenditureEditErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <select
          data-testid={`triage-ledger-category-select-${id}`}
          aria-label="Category"
          value={expenditure.category}
          disabled={saving}
          onChange={(e) =>
            void handleCategoryChange(e.target.value as ExpenditureCategory)
          }
          className={cn(financeFormSelectClass, 'w-fit')}
        >
          {/* An imported row's current category may be one of
              NON_SELECTABLE_EXPENDITURE_CATEGORIES, so render it as a disabled current
              value (else a native <select> would silently show the first real option and
              read as "Operating"). */}
          {!EXPENDITURE_CATEGORY_OPTIONS.includes(expenditure.category) && (
            <option value={expenditure.category} disabled>
              {expenditureCategoryLabel(expenditure.category, settings?.rulesetCode)}
            </option>
          )}
          {EXPENDITURE_CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {expenditureCategoryLabel(c, settings?.rulesetCode)}
            </option>
          ))}
        </select>
        {saving && (
          <InlineSpinner
            testId={`triage-ledger-category-saving-${id}`}
            label="Saving"
          />
        )}
      </div>
      {categoryError && (
        <p
          role="alert"
          data-testid={`triage-ledger-category-error-${id}`}
          className="text-xs text-rose-700"
        >
          {categoryError}
        </p>
      )}
    </div>
  );
}

/**
 * Inline vendor reassignment picker. Reuses the classification contact flow:
 * searches existing payee contacts and offers a quick-create.
 */
function ExpenditureVendorPicker({
  id,
  onSelect,
  onCancel,
}: {
  id: string;
  onSelect: (contactId: string) => void;
  onCancel: () => void;
}) {
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useFinanceContacts({
    pageSize: 100,
    type: ContactType.Payee,
    search: search.trim().length > 0 ? search.trim() : undefined,
  });
  const results = data?.items ?? [];

  const { mutateAsync: createContactAsync, isPending: isCreating } =
    useCreateFinanceContact();

  const { data: profile } = useComplianceProfile();

  const handleCreate = async (request: CreateFinanceContactRequest) => {
    setError(null);
    try {
      const created = await createContactAsync(request);
      onSelect(created.contact.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create vendor.');
    }
  };

  return (
    <div
      data-testid={`triage-ledger-vendor-picker-${id}`}
      className="flex w-full flex-col gap-1"
    >
      <input
        type="text"
        autoFocus
        data-testid={`triage-ledger-vendor-search-${id}`}
        placeholder="Search or create vendor"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
        className={cn(financeFormInputClass, 'w-full')}
      />
      {!showCreate && results.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded border">
          {results.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                data-testid={`triage-ledger-vendor-option-${c.id}`}
                className={cn(financeTextActionClass, 'w-full justify-start text-left hover:bg-accent')}
                onClick={() => onSelect(c.id)}
              >
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!showCreate && (
        <button
          type="button"
          data-testid={`triage-ledger-vendor-create-${id}`}
          className={cn(financeTextActionClass, 'w-fit text-primary hover:bg-primary/10 hover:underline')}
          onClick={() => setShowCreate(true)}
        >
          + Create new vendor
        </button>
      )}
      {showCreate && (
        <InlineContactForm
          contactType={ContactType.Payee}
          initialName={search}
          showEmployerOccupation={false}
          supportedOrganizationTypes={profile?.supportedOrganizationTypes ?? []}
          addressAutocomplete
          isSaving={isCreating}
          onSave={handleCreate}
          onCancel={() => setShowCreate(false)}
        />
      )}
      {error && (
        <p role="alert" className="text-xs text-rose-700">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Maps an expenditure PATCH failure to treasurer copy — switches on the
 * `ContactNotFound` wire code, with the standard stale-row message for a 409.
 */
function expenditureEditErrorMessage(err: unknown): string {
  if (isStaleWrite(err)) return REFRESH_PROMPT;
  if (
    err instanceof Error &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string'
  ) {
    const code = (err as { code: string }).code;
    if (code === ErrorCode.StagedRowUpdateContactNotFound) {
      return "That vendor couldn't be found — refresh and try again.";
    }
  }
  return err instanceof Error ? err.message : 'Failed to save.';
}

/**
 * Date cell — DISPLAY-ONLY in the dense register (ADR-016, 2026-06-25). Editing
 * moved into the expanded row detail (`detailFields`), so the register row stays
 * scannable. BankLine dates are always the imported statement record.
 */
function DateCell({ item }: { item: TriageItem }) {
  switch (item.kind) {
    case 'Contribution':
      return <>{formatDate(item.contribution.date)}</>;
    case 'Expenditure':
      return <>{formatDate(item.expenditure.date)}</>;
    case 'Refund':
      return <>{formatDate(item.refund.refundedAt)}</>;
    case 'BankLine':
      return <>{formatDate(item.date)}</>;
    default:
      return assertNever(item);
  }
}

/**
 * Amount cell — DISPLAY-ONLY in the dense register (ADR-016, 2026-06-25). Editing
 * moved into the expanded row detail (`detailFields`). Expenditure amount was
 * never editable and stays so.
 */
function AmountCell({ item }: { item: TriageItem }) {
  const isIn = triageItemDirection(item) === 'in';
  // Magnitude only — direction is carried by the sign + color, not the stored
  // sign (bank debits are stored negative; every other kind is positive). The
  // '−' is the typographic minus, not a hyphen, so it aligns in the mono
  // register column.
  const magnitude = formatAmount(Math.abs(rawAmount(item)));
  return (
    <span
      className={cn('font-medium', isIn ? 'text-emerald-700' : 'text-rose-700')}
    >
      {isIn ? '+' : '−'}
      {magnitude}
    </span>
  );
}

/** The stored amount for a triage item, sign and all (bank debits are negative). */
function rawAmount(item: TriageItem): number {
  switch (item.kind) {
    case 'BankLine':
      return item.bt.amount;
    case 'Contribution':
      return item.contribution.amount;
    case 'Expenditure':
      return item.expenditure.amount;
    case 'Refund':
      return item.refund.amount;
    default:
      return assertNever(item);
  }
}

/**
 * Click-to-edit cell: the value renders as a button; clicking swaps in an
 * input. Enter or blur saves (no-change skips the request), Escape cancels.
 */
function InlineEditCell({
  id,
  field,
  value,
  display,
  onSave,
  editLabel,
}: {
  id: string;
  field: 'desc' | 'amount' | 'date';
  value: string;
  display: string;
  onSave: (next: string) => Promise<void>;
  /**
   * A human word for the field ("amount", "date", "description", "reason",
   * "purpose") used in the "Edit {label}" accessible label/title. Falls back to
   * the engineer-facing `field` token when a caller doesn't supply one.
   */
  editLabel?: string;
}) {
  const fieldLabel = editLabel ?? field;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Every save is an HTTP round-trip; a spinner (in place of the pencil) tells the
  // treasurer the edit registered while the PATCH is in flight — matching the
  // payee/category editors.
  const [saving, setSaving] = useState(false);
  // Dates/amounts use the register's monospace so a value keeps the same
  // typeface whether the row is collapsed (mono register cell) or expanded
  // (this detail editor). Descriptions/reasons/purposes stay sans (`desc`).
  const numeric = field === 'amount' || field === 'date';

  const commit = async () => {
    setEditing(false);
    if (draft === value) return;
    setErrorMessage(null);
    setSaving(true);
    try {
      await onSave(draft);
    } catch (err) {
      setErrorMessage(
        isStaleWrite(err)
          ? REFRESH_PROMPT
          : err instanceof Error
            ? err.message
            : 'Failed to save.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div>
        <button
          type="button"
          data-testid={`triage-ledger-edit-${field}-${id}`}
          className={cn(
            financeInlineEditTriggerClass,
            numeric && 'font-mono',
          )}
          aria-label={`Edit ${fieldLabel}`}
          title={`Edit ${fieldLabel}`}
          disabled={saving}
          onClick={() => {
            setDraft(value);
            setEditing(true);
          }}
        >
          {display}
          <InlineEditIcon
            saving={saving}
            savingTestId={`triage-ledger-saving-${field}-${id}`}
          />
        </button>
        {errorMessage && (
          <p
            role="alert"
            data-testid={`triage-ledger-edit-error-${field}-${id}`}
            className="mt-0.5 text-xs text-rose-700"
          >
            {errorMessage}
          </p>
        )}
      </div>
    );
  }

  return (
    <input
      data-testid={`triage-ledger-${field}-input-${id}`}
      autoFocus
      type={field === 'amount' ? 'number' : field === 'date' ? 'date' : 'text'}
      step={field === 'amount' ? '0.01' : undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') void commit();
        if (e.key === 'Escape') setEditing(false);
      }}
      className={cn(
        financeFormInputClass,
        numeric && 'font-mono',
      )}
    />
  );
}

/**
 * True when an event originated on an interactive control inside a ledger row
 * (a button / link / form field / combobox). The row-level click + keyboard
 * disclosure toggle uses this to bow out, so those controls run their own
 * action instead of also opening/closing the row detail.
 */
function isInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    // The Radix Select popup renders its listbox + options in a PORTAL (at
    // document.body), so an option click is NOT a DOM descendant of the detail and
    // the `isExpandedDetailTarget` guard misses it — picking an option would collapse
    // the row (fatal for the in-kind capture's staged draft, annoying for every other
    // detail Select). Recognize the popup content so the row toggle bows out.
    target.closest(
      'button, a, input, select, textarea, [role="combobox"], [role="option"], [role="listbox"], [data-radix-popper-content-wrapper]',
    ) !== null
  );
}

function isExpandedDetailTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('[data-ledger-row-detail="true"]') !== null
  );
}

function shouldIgnoreRowDisclosureToggle(target: EventTarget | null): boolean {
  return isInteractiveTarget(target) || isExpandedDetailTarget(target);
}

// The inner 6-column grid template that reconstructs the register columns
// inside a single `<td colSpan={7}>` (ADR-026 PR-C — single-`<tr>` merge, § 5).
// The SAME template drives the header row (also one `colSpan={7}` cell), so the
// two share one column system and align by construction. Tracks are fully
// DETERMINED — no content-based `auto` — precisely because the header and body
// are SEPARATE grids: an `auto`/`max-content` track sizes to each grid's own
// content and the two drift apart.
// Fixed gutters + Date + Amount, and proportional `fr` for
// Payee/contributor + Category (`fr` distributes identical leftover space in
// both grids, so those columns stay aligned even though their content differs):
// checkbox gutter (`3rem` — a 44px WCAG/Apple touch target for the status
// checkbox, so an older user aiming for it doesn't miss into the row-expand),
// Date, a Payee column, and the widest column, Category, because it carries a classification
// hint plus its Accept and must show the hint in full. Both have real minimums so a narrow
// window (iPad, heavy zoom) scrolls the table sideways instead of crushing them; then a
// right-aligned
// Amount, and the action column (6rem — wide enough for a LABELED trailing
// quick-action, "Exclude" / "Restore", rather than a bare glyph that read as
// "delete"). Exported so the pinned calculated-processor-fee row
// (`ProcessorFeeLedgerRow`) lays its cells out on the SAME track template — it
// reads as a register line aligned under the ledger's Date / Payee /
// Category / Amount columns, not a banner.
export const LEDGER_ROW_GRID_TEMPLATE =
  '3rem 6rem minmax(10rem,1.3fr) minmax(16rem,2fr) 7rem 6rem';

// The cold-load placeholder for the ledger. Renders the real header row (labels
// show immediately) over a handful of pulsing skeleton rows laid out on the SAME
// track template as a data row, so the columns line up and the switch to real
// data doesn't shift the layout. Symbols/labels only — no interactivity.
export function TriageLedgerSkeleton() {
  return (
    <div
      data-testid="triage-ledger-loading"
      aria-busy="true"
      aria-label="Loading transactions"
      className="mt-4"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead colSpan={7} className="h-auto p-0">
              <div
                className="grid items-center gap-x-2 p-2"
                style={{ gridTemplateColumns: LEDGER_ROW_GRID_TEMPLATE }}
              >
                <span aria-hidden />
                <span>Date</span>
                <span>Payee / contributor</span>
                <span>Category</span>
                <span className="text-right">Amount</span>
                <span aria-hidden />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 8 }, (_, i) => i).map((i) => (
            <TableRow key={i} aria-hidden>
              <TableCell colSpan={7} className="p-0">
                <div
                  className="grid items-center gap-x-2 p-2"
                  style={{
                    gridTemplateColumns: LEDGER_ROW_GRID_TEMPLATE,
                    height: LEDGER_ROW_ESTIMATE_PX,
                  }}
                >
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-16 justify-self-end" />
                  <span aria-hidden />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const TriageLedgerRow = memo(function TriageLedgerRow({
  item,
  expanded,
  highlighted,
  recentlyCollapsed,
  justCompleted,
  duplicateFlag,
  hasOrgTypeConflict,
  purposeFlagged,
  onToggle,
  onCollapse,
  onClassifiedFollowUp,
  periodNote,
  rowRef,
  dataIndex,
}: {
  item: TriageItem;
  expanded: boolean;
  /**
   * An Open duplicate-contact flag whose pair includes this row's contact — the
   * contributor on a Contribution row or the payee on an Expenditure row. Drives
   * the collapsed "possible duplicate of …" badge and, when expanded, the inline
   * `DuplicateResolvePanel` at the top of the body. Absent on every other row.
   */
  duplicateFlag?: DuplicateContactFlagDto;
  /**
   * True when this row's contact (contributor or payee) has an unresolved contact-merge
   * OrganizationType conflict (#350). Drives the collapsed "organization type conflict" badge
   * that links to the contact page, where the one-tap fix-it alert resolves it.
   */
  hasOrgTypeConflict?: boolean;
  /**
   * True when the filing preflight's disbursement-purpose verdict named THIS row — a
   * blank or regulation-named-generic purpose on a disbursement that will itemize.
   * A plain boolean rather than the id set so a verdict change re-renders only the
   * rows whose own answer moved.
   */
  purposeFlagged?: boolean;
  /**
   * Deep-link arrival highlight: a brief ring/wash on the row a report-detail
   * link jumped to, so the treasurer's eye lands on the exact transaction.
   * Fades after ~2s (cleared by the parent's one-shot effect).
   */
  highlighted: boolean;
  /**
   * Short-lived row-memory wash after the treasurer closes this row's inline
   * details. Paired with focus restoration so layout shift does not erase context.
   */
  recentlyCollapsed: boolean;
  /**
   * One-shot success wash: true for ~1s right after this row settled into Done,
   * so the treasurer keeps their place when it collapses out of the queue.
   */
  justCompleted: boolean;
  /**
   * Optional context tag for rows that belong to a DIFFERENT period than the
   * one being worked (e.g. later-period rows in the expanded next-period
   * bucket). Rendered as a small mono badge above the description.
   */
  periodNote?: string;
  /**
   * The virtualizer's `measureElement` ref (ADR-026 PR-C). Lands on the single
   * `<tr>` so the virtualizer measures this row's full height — including its
   * expansion body, since the two former sibling `<tr>`s are now one measured
   * element. Absent under the jsdom render-all degradation (no layout engine).
   */
  rowRef?: (node: Element | null) => void;
  /** The row's index in the flat `RenderRow[]`, for the virtualizer. */
  dataIndex?: number;
  /**
   * Stable, id-taking toggle/collapse from the parent (root cause #2). The parent
   * passes ONE stable callback shared by every row and this row binds it to its own
   * id, so a sibling's expand/collapse never changes THIS row's props — letting
   * React.memo bail out on every row that didn't change. Re-exposed to the row's
   * children as the zero-arg toggle/collapse contract they expect
   * (handleToggleExpand / handleCollapse below).
   */
  onToggle: (id: string) => void;
  onCollapse: (id: string) => void;
  /**
   * Carries the id of the entity a classify just created up to the ledger, so it
   * can open that new row if it still needs work rather than collapsing and forcing
   * a re-click. Stable (a parent useCallback), so it never busts the memo.
   */
  onClassifiedFollowUp?: (newEntityId?: string) => void;
}) {
  // Bind the shared, stable parent callbacks to this row's id. Recreated per row
  // render, but the row only re-renders when its own props change (the memo above),
  // so these closures never reach an unrelated row.
  const handleToggleExpand = () => onToggle(item.id);
  const handleCollapse = () => onCollapse(item.id);
  // A row off the report renders dimmed/struck — accounted for but kept visible
  // forever. Keys on the "Not reported" lens (isNotReportedResolution): a
  // bank line Excluded from the report (ADR-014) OR a Voided money row (ADR-016).
  const dimmed = isNotReportedResolution(item.resolvedAs);
  // The contact this row's duplicate flag is keyed on (contributor or payee); see
  // rowDuplicateContactId. `isPayee` switches the resolve panel's copy from
  // donor-facing to payee-facing (see DuplicateResolvePanel).
  const rowContactId = rowDuplicateContactId(item);
  const isPayee = item.kind === 'Expenditure';
  // Flagged = a row that can't be checked until something is resolved (the amber
  // "!" cases: Classify / ProvideDonorInfo / ProvideExpenditureInfo / MatchRefund).
  // Confirm-due (empty-circle) and Done/Reported rows are NOT flagged. These get a
  // faint amber LEFT wash so the eye finds the attention rows without scanning the
  // status column. A soft left-fading background, not a hard side stripe (banned).
  const flagged = item.status === 'NeedsAction' && item.actionNeeded !== 'Confirm';
  // ADR-071 amendment (#575) — the boundary straggler: an unclassified bank line that dates into an
  // already-CLOSED period (derived server-side as `bt.inClosedPeriod`). It landed on the ledger loud,
  // not silent, so it gets variant C — a distinct amber band (left stripe) + a "Needs amendment" mono
  // tag, on top of the shared needs-action wash it already carries as a NeedsAction/Classify row.
  const isStraggler = item.kind === 'BankLine' && !!item.bt?.inClosedPeriod;
  // A row the treasurer just marked "reported" keeps its acknowledgment + Undo
  // mounted even though the optimistic flip already stamped it Reported (which
  // otherwise flips `canOfferMarkAsReported` false and drops the affordance).
  const justMarkedReported = useIsJustMarkedReported(item.id);
  // The row the most recent classification-hint Accept created keeps a faint success wash while
  // it holds the Undo.
  const lastAccepted = useIsLastAcceptedHint(item);
  // ADR-026 PR-C — SINGLE measured `<tr>` per item. The virtualizer measures one
  // element per index, so the former detached sibling expansion `<tr>` is folded
  // INTO this row: one `<td colSpan={7}>` carries both the 7-column summary grid
  // and (when expanded) the expansion body, so the measured height already
  // includes the expansion and the spacer math never clips or double-counts it.
  // Two consequences we accept for this internal treasurer table (not silently):
  //   (1) native column sizing becomes NOMINAL — the browser's header→body
  //       `<col>` width negotiation no longer spans the single merged `<td>`, so
  //       the inner grid's own track template (LEDGER_ROW_GRID_TEMPLATE) is what
  //       lines the cells up under the `<thead>` labels.
  //   (2) per-cell↔`<th>` a11y association is lost — the grid cells aren't real
  //       `<td>`s scoped to their `<th>`, so a screen reader won't announce
  //       "Amount: -$480.00" per cell. Row text content stays intact.
  return (
    <TableRow
      ref={rowRef}
      data-index={dataIndex}
      data-testid={`triage-ledger-row-${item.id}`}
      data-recently-collapsed={recentlyCollapsed || undefined}
      // ADR-016 step 1: the row is a disclosure — click it (or Enter/Space
      // while focused) to open its read-only detail. Clicks/keys that
      // originate on an interactive control inside the row (ready checkbox,
      // confirm / exclude / restore buttons, the category select, inline-edit
      // fields, links) are left to that control; the row toggle bows out.
      tabIndex={0}
      aria-expanded={expanded}
      onClick={(e) => {
        if (shouldIgnoreRowDisclosureToggle(e.target)) return;
        handleToggleExpand();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (shouldIgnoreRowDisclosureToggle(e.target)) return;
        e.preventDefault();
        handleToggleExpand();
      }}
      className={cn(
        'border-border transition-colors duration-1000 cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        dimmed && 'opacity-60',
        // The amber attention wash lives on the SUMMARY line only (below), not
        // here on the whole row — otherwise it bleeds down through the expansion
        // body (one merged cell per ADR-026 PR-C) and tints the editor.
        // Theme ring/accent wash — no arbitrary hex. The duration-1000
        // transition lets the ring fade out smoothly when `highlighted`
        // drops to false.
        highlighted && 'bg-accent ring-2 ring-inset ring-ring',
        recentlyCollapsed && 'bg-amber-50 ring-2 ring-inset ring-amber-200',
        // One-shot success wash the moment the row settles into Done (the Done
        // checkmark in the status column is the paired non-color signal).
        justCompleted && 'animate-ledger-complete',
        lastAccepted && 'bg-success/6',
      )}
      data-just-completed={justCompleted || undefined}
      data-last-accepted={lastAccepted || undefined}
    >
      <TableCell colSpan={7} className="p-0 align-top">
        {/* The 7-column summary grid, reconstructed inside the single merged
            cell so it lines up under the `<thead>` labels. `p-2` mirrors the
            per-cell padding the native `<TableCell>`s used to carry. */}
        <div
          data-testid={`triage-ledger-summary-${item.id}`}
          className={cn(
            'grid items-start gap-x-2 p-2',
            // Faint amber left wash on attention rows, scoped to the summary line
            // so it never bleeds into the expansion editor. Deepens on hover;
            // suppressed while highlighted so the deep-link wash wins.
            flagged &&
              !highlighted &&
              'bg-gradient-to-r from-amber-100/60 to-transparent to-[30%] hover:from-amber-100',
            // Variant C (#575) — a straggler is a DISTINCT band: a solid amber left stripe marks it
            // apart from an ordinary needs-action row (which carries only the soft fade). Suppressed
            // while highlighted so the deep-link ring wins.
            isStraggler && !highlighted && 'border-l-2 border-amber-400',
          )}
          style={{ gridTemplateColumns: LEDGER_ROW_GRID_TEMPLATE }}
        >
          <div className="align-top">
            <ReadyCheckbox
              item={item}
              expanded={expanded}
              onToggleExpand={handleToggleExpand}
            />
          </div>
          <div
            className={cn(
              'whitespace-nowrap align-top font-mono text-xs text-muted-foreground',
              dimmed && 'line-through',
            )}
          >
            <DateCell item={item} />
          </div>
          <div className="min-w-0 align-top text-sm">
            {/* Later-period context tag — appears when the row belongs to a
                future period that is NOT part of the current filing. */}
            {periodNote && (
              <span className="mb-0.5 block text-[9.5px] font-mono text-slate-400 tracking-[.04em]">
                {periodNote}
              </span>
            )}
            {/* #591 — the shared bank-line info-header: the straggler "Needs amendment"
                tag, the "Not yet cleared" marker, and the lingering-duplicate hint, all
                from the one component the expanded detail + triage popup also compose, so
                a signal added once appears on every surface. */}
            {item.kind === 'BankLine' && (
              <BankLineInfoHeader
                id={item.id}
                bt={item.bt}
                surface="row"
                className="mb-0.5"
              />
            )}
            <div className={cn(dimmed && 'line-through')}>
              <DescriptionCell item={item} />
            </div>
            {/* Possible-duplicate badge — this row's contact (contributor OR payee)
                is one side of an Open duplicate pair. Names the OTHER side so the
                treasurer knows which record it collides with; expanding the row
                opens the resolve panel. */}
            {duplicateFlag && rowContactId && (
              <DuplicateBadge flag={duplicateFlag} rowContactId={rowContactId} />
            )}
            {/* Organization-type conflict badge (#350) — this row's contact carries an unresolved
                contact-merge type conflict. Links to the contact page, where the one-tap fix-it
                alert resolves it (the resolve surface is the contact record, not the ledger row). */}
            {hasOrgTypeConflict && rowContactId && (
              <OrgTypeConflictBadge rowContactId={rowContactId} />
            )}
          </div>
          <div className="min-w-0 align-top text-sm">
            <ExpenditureCategoryCell
              item={item}
              purposeFlagged={purposeFlagged}
              onReviewHint={() => {
                if (!expanded) handleToggleExpand();
              }}
            />
          </div>
          <div
            className={cn(
              'whitespace-nowrap text-right align-top font-mono text-sm',
              dimmed && 'line-through',
            )}
          >
            <AmountCell item={item} />
          </div>
          <div className="text-right align-top">
            <TrailingCell item={item} />
          </div>
        </div>
        {/* Expansion body — a `<div>` (NOT a second `<tr>`/`<td>`) so the whole
            row stays ONE measured virtualizer index. Keeps the
            `triage-ledger-expanded-${id}` testid so `getByTestId(...)` +
            `.querySelector(...)` inside it still resolve. */}
        {expanded && (
          <div
            data-testid={`triage-ledger-expanded-${item.id}`}
            data-ledger-row-detail="true"
            className="bg-muted/20 p-4 animate-ledger-expand"
          >
            {/* #591 — the same shared bank-line info-header the row + popup compose, so
                a pending line no longer looks fully settled once opened from the ledger.
                Pinned at the top of the detail and rendered regardless of whether an
                action panel occupies the body below (the panel suppresses only the
                read-only field list). */}
            {item.kind === 'BankLine' && (
              <BankLineInfoHeader
                id={item.id}
                bt={item.bt}
                surface="detail"
                className="mb-3"
              />
            )}
            {/* Duplicate resolve panel — pinned at the TOP of the expanded body for
                a flagged Contribution (donor) OR Expenditure (payee) row. onDone
                collapses the row; the ledger refreshes and the badge clears once the
                flag is resolved. */}
            {duplicateFlag && rowContactId && (
              <DuplicateResolvePanel
                flag={duplicateFlag}
                rowContactId={rowContactId}
                isPayee={isPayee}
                onDone={handleCollapse}
              />
            )}
            <FlagBanner item={item} />
            <RowLockBanner item={item} />
            {item.kind === 'Contribution' && item.checkImage && (
              <ContributionCheckImage
                contributionId={item.contribution.id}
                fileName={item.checkImage.fileName}
              />
            )}
            <ExpandedBody
              item={item}
              onCollapse={handleCollapse}
              onClassifiedFollowUp={onClassifiedFollowUp}
            />
            {/* When an action panel is up, it already states the row's fields;
                the read-only detail list would just restate them in a different
                visual language, so we suppress it (single predicate keeps the
                panel and the list from drifting). Delete stays reachable. */}
            {!hasActionPanel(item) && <RowDetailSummary item={item} />}
            {item.kind === 'Contribution' && (
              <ResolutionChainView contributionId={item.contribution.id} />
            )}
            {/* AL EDI delta-export (§6): a Done (Confirmed) money row can be
                marked reported when the treasurer entered it directly in the AL
                portal — removing it from the export pool so we never re-export
                and duplicate it (ADR-020). Not offered on a not-reported (voided
                / excluded) row, nor on an already-Reported row (canOfferMarkAsReported
                gates on the stamped ledgerStatus — a filed-but-open-period row reads
                Done yet is already Reported, ADR-019 D6). */}
            {(item.kind === 'Contribution' || item.kind === 'Expenditure') &&
              item.status === 'Done' &&
              !isNotReportedResolution(item.resolvedAs) &&
              // `|| justMarkedReported` keeps the acknowledgment + Undo mounted
              // through the optimistic Reported flip that just turned
              // canOfferMarkAsReported false.
              (canOfferMarkAsReported(item) || justMarkedReported) && (
                <MarkAsReportedAction item={item} />
              )}
            <RowDetailActions item={item} />
          </div>
        )}
      </TableCell>
    </TableRow>
  );
});

/**
 * The leading control = "ready to report". Dispatches on the server-computed
 * `status` + `actionNeeded` + `resolvedAs` triple (design doc § 5.1):
 *
 *  - `Reported`                  → lock, inert.
 *  - `Done` + bank not-reported  → dimmed indicator (restore lives in the
 *                                  trailing ↺); not a green check.
 *  - `Done` (any other)          → green ✓; click unchecks (unconfirm / reopen /
 *                                  unlink / revoke — `DoneActionCell`).
 *  - `NeedsAction` + `Confirm`   → empty circle; click confirms.
 *  - `NeedsAction` (flagged)     → amber circle + flag text; click expands the
 *                                  existing per-kind body.
 */
function ReadyCheckbox({
  item,
  expanded,
  onToggleExpand,
}: {
  item: TriageItem;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  if (item.status === 'Reported') return <ReportedActionCell id={item.id} />;
  // An upload-locked money row (Contribution / Expenditure) derives to Done (calendar
  // lock is future) but is frozen on the portal side — route it to the same inert cell
  // as a filed row, NOT the interactive DoneActionCell (whose unconfirm/uncheck gesture
  // 400s on an uploaded row).
  if (
    (item.kind === 'Contribution' || item.kind === 'Expenditure') &&
    moneyRowAffordances(item.freeze).lock === 'UploadedToPortal'
  ) {
    return <ReportedActionCell id={item.id} />;
  }
  // Same inert lock for a row a filing stamped Reported whose period an amendment reopened:
  // it is on file, so the uncheck gesture (which the server refuses) is not offered.
  if (isMoneyRowStampedReported(item)) {
    return <ReportedActionCell id={item.id} detail={REOPENED_PERIOD_REPORTED_NOTE} />;
  }
  if (item.status === 'Done') {
    // ADR-016: a voided money row carries status:'Done' too. The not-reported
    // branch must precede the DoneActionCell fallthrough so a voided row shows
    // the dimmed ⊘ and is NOT routed to the unconfirm/reopen uncheck path.
    if (isNotReportedResolution(item.resolvedAs)) {
      // Symbols only in the status column — a dimmed circled-slash; the
      // "Not reported" word shows on hover (tooltip) + the accessible label,
      // never as visible text.
      return (
        <StatusTooltip label={resolvedAsLabel(item.resolvedAs) ?? 'Not reported'}>
          <span
            data-testid={`triage-ledger-notreported-${item.id}`}
            aria-label={resolvedAsLabel(item.resolvedAs) ?? 'Not reported'}
            className="inline-flex h-6 w-6 items-center justify-center text-lg text-muted-foreground/60"
          >
            ⊘
          </span>
        </StatusTooltip>
      );
    }
    return <DoneActionCell item={item} />;
  }
  // NeedsAction
  if (item.actionNeeded === 'Confirm') {
    return <ConfirmCheckbox item={item} />;
  }
  return (
    <FlagCheckbox item={item} expanded={expanded} onToggleExpand={onToggleExpand} />
  );
}

/**
 * Empty-circle confirm checkbox for a Confirm-due row (a Pending contribution
 * or born-Pending expenditure). Click confirms — flips the row to Done. Keeps
 * the stable `triage-ledger-confirm-{id}` testid so a Confirm-due row's gesture
 * survives the redesign.
 */
function ConfirmCheckbox({ item }: { item: TriageItem }) {
  const { errorMessage, run, errorId, isRunning } = useQuickAction(item.id);
  const reducedMotion = useReducedMotion();
  const { mutateAsync: confirmContributionAsync, isPending: contribPending } =
    useConfirmContribution();
  const { mutateAsync: confirmExpenditureAsync, isPending: expPending } =
    useConfirmExpenditure();
  // `isRunning` is the row-scoped in-flight flag (survives the optimistic control
  // swap) — it keeps this checkbox disabled through a rapid select/deselect so a
  // second click can't fire an overlapping mutation carrying a stale version.
  const isPending = contribPending || expPending || isRunning;

  const confirm = () => {
    switch (item.kind) {
      case 'Contribution':
        return run(
          () =>
            confirmContributionAsync({
              row: item.contribution,
            }),
          'Failed to confirm contribution.',
        );
      case 'Expenditure':
        return run(async () => {
          try {
            await confirmExpenditureAsync({
              row: item.expenditure,
            });
          } catch (err) {
            throw new Error(confirmExpenditureErrorMessage(err));
          }
        }, 'Failed to confirm this expenditure.');
      case 'BankLine':
      case 'Refund':
      default:
        // Only contributions / expenditures are ever Confirm-due.
        return Promise.resolve();
    }
  };

  return (
    <div className="flex flex-col items-start gap-0.5">
      <button
        type="button"
        data-testid={`triage-ledger-confirm-${item.id}`}
        aria-label="Mark ready to report"
        disabled={isPending}
        onClick={confirm}
        // 44px tap target (older users mis-tapped the 20px circle into the
        // row-expand); the visible circle stays 20px inside it. On a rejected
        // flip the circle snaps back with a brief motion (unless reduced).
        className={cn(
          'group inline-flex h-11 w-11 items-center justify-center rounded-full disabled:opacity-50',
          errorMessage && !reducedMotion && 'animate-ledger-revert',
        )}
      >
        <span
          aria-hidden="true"
          className="h-5 w-5 rounded-full border-2 border-muted-foreground/40 group-hover:border-emerald-600 group-hover:bg-emerald-50"
        />
      </button>
      <RowError id={errorId} message={errorMessage} onRetry={confirm} />
    </div>
  );
}

/**
 * Amber flag checkbox for a row that can't yet be checked — it needs a
 * resolution first (Classify / ProvideDonorInfo / ProvideExpenditureInfo /
 * MatchRefund). Click expands the existing per-kind body where the real
 * resolution happens. The flag text is treasurer copy at the render boundary.
 */
function FlagCheckbox({
  item,
  expanded,
  onToggleExpand,
}: {
  item: TriageItem;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  // Symbols only — the flag's guidance (e.g. "Needs payee address") shows on
  // hover (tooltip) + the accessible label, never as visible text in the
  // status column. Click expands the per-kind body where the resolution happens.
  const guidance = flagLabel(item);
  return (
    <StatusTooltip label={guidance}>
      <button
        type="button"
        data-testid={`triage-ledger-flag-${item.id}`}
        aria-label={guidance}
        aria-expanded={expanded}
        onClick={onToggleExpand}
        // 44px tap target; the visible amber "!" circle stays 24px inside it.
        className="group inline-flex h-11 w-11 items-center justify-center rounded-full"
      >
        <span
          aria-hidden="true"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-amber-500 text-xs font-bold text-amber-600 group-hover:bg-amber-50"
        >
          !
        </span>
      </button>
    </StatusTooltip>
  );
}

/**
 * Repeats the amber flag's guidance as a banner at the top of a flagged row's
 * expansion — so once the row is open (and the leading tooltip is gone) the
 * treasurer still sees exactly what's needed ("Pick a type", "missing
 * occupation & address", …). Renders nothing for non-flagged rows (Confirm-due
 * / Done / Reported), whose expansions — when they exist — aren't "what's
 * missing" prompts.
 */
function FlagBanner({ item }: { item: TriageItem }) {
  const { data: profile } = useComplianceProfile();
  const text = flagBannerText(item, profile?.stateDisplayName ?? null);
  if (!text) return null;
  return (
    <div
      data-testid={`triage-ledger-flag-banner-${item.id}`}
      className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
    >
      <span
        aria-hidden="true"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-amber-500 text-xs font-bold text-amber-600"
      >
        !
      </span>
      <span>{text}</span>
    </div>
  );
}

/**
 * The read-only lock banner at the top of a frozen money row's expanded detail. A locked
 * Contribution/Expenditure (e.g. a booked processor fee already on an uploaded export, or any
 * row in a closed period) is frozen — the inline field editors and the permanent Delete
 * are suppressed on such a row, but nothing states WHY up front. This banner is that
 * upfront signal: a quiet lock + status line that reads read-only BEFORE any edit
 * attempt, so the row never invites a dead-end edit that only refuses on commit
 * (mirrors the backend lock, which stays a 400 fallback).
 *
 * Fires on the lock {@link moneyRowAffordances} names: the calendar lock or the portal
 * upload. A voided row is frozen too but is not locked, so it gets no banner.
 *
 * The two locks get DIFFERENT copy, because they mean different things: the upload-lock says
 * this row is with the regulator, while the calendar lock only says its period is reported
 * history (filed and past its deadline, or before onboarding) and cannot say where this row is
 * on file (GitHub issue #659). rowLockCopy owns that split, including the chip word.
 *
 * Bank lines are never filed here, and a Refund row has no filed-lock semantics, so
 * this is money-rows-only (Contribution / Expenditure). The lock copy is
 * jurisdiction-resolved at the render boundary via usePortalUploadModel() — AL names
 * its FCPA portal and its add-only correction rule, federal stays neutral — so the
 * shared backend refusal message can be, and is, jurisdiction-neutral.
 */
function RowLockBanner({ item }: { item: TriageItem }) {
  // Reopen is gated on Finance:Manage, so only a manager is pointed at it.
  const canManageFinance = useCampaignStore((s) => s.canManage('Finance'));
  const reopenReportId = canManageFinance ? item.lockingReportId : null;
  const lock =
    item.kind === 'Contribution' || item.kind === 'Expenditure'
      ? moneyRowAffordances(item.freeze).lock
      : null;
  const copy = rowLockCopy(usePortalUploadModel(), {
    uploadLocked: lock === 'UploadedToPortal',
    reopenable: !!reopenReportId,
  });
  if (lock === null) return null;
  return (
    <div
      data-testid={`triage-ledger-filed-banner-${item.id}`}
      className="mb-3 flex items-start gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
    >
      <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        <span className="mr-1.5 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-foreground/70">
          {copy.label}
        </span>
        {copy.detail}
        {reopenReportId && copy.reopenLinkLabel && (
          <>
            {' '}
            <Link
              to="/admin/finance/reports/$reportId"
              params={{ reportId: reopenReportId }}
              data-testid={`triage-ledger-reopen-link-${item.id}`}
              className="font-medium text-foreground underline underline-offset-2"
            >
              {copy.reopenLinkLabel}
            </Link>
          </>
        )}
      </span>
    </div>
  );
}

/**
 * The trailing column. Bank lines get the ADR-014 exclude (✕) / restore (↺)
 * pair; live money rows (Contribution / Expenditure) get the SAME shape — a
 * one-click Exclude (which voids the row, reversibly) and, once excluded, a
 * Restore (un-void). Permanent Delete is NOT here: it's the deliberate second
 * step inside the row detail (ADR-016 step 2).
 *
 * Contribution rows additionally carry a Refund affordance — the entry point
 * for the refund flow (RefundDialog → useRefundContribution), which otherwise
 * had no reachable trigger. A refund does NOT mutate the original gift (it's
 * filed gross); the button is offered on any LIVE contribution, including a
 * Reported one (a filed gift is the real-world refund target), but not on a
 * Voided contribution (not a live gift) or on a Refund row itself.
 */
function TrailingCell({
  item,
}: {
  item: TriageItem;
}) {
  if (item.kind === 'BankLine') {
    // A NeedsAction bank line can be excluded from the report (ADR-014:
    // personal, a transfer, or a mistake); an excluded one can be restored.
    // Both are bank-line-only resolutions. Legacy-tag tolerant for the restore.
    if (item.status === 'NeedsAction') {
      return <BankExcludeButton item={item} />;
    }
    if (isNotReportedResolution(item.resolvedAs)) {
      return <BankRestoreButton item={item} />;
    }
    return null;
  }
  // Money rows. A refund row has no trailing quick-action here (delete lives in
  // its detail; the ✓-uncheck stays in the status column).
  if (item.kind === 'Refund') return null;

  // A voided row offers Restore, a live one the reversible Exclude, each as `freeze` allows.
  // Refund is NOT a trailing quick-action: it's rare and heavy (moves real money, books a
  // separate row), so it lives with Delete in the row detail (RowDetailActions).
  const { void: canVoid, unvoid: canUnvoid } = moneyRowAffordances(item.freeze);
  if (isNotReportedResolution(item.resolvedAs)) {
    return canUnvoid ? <MoneyRowRestoreButton item={item} /> : null;
  }
  return canVoid ? <MoneyRowExcludeButton item={item} /> : null;
}

/**
 * Whether a contribution row can still be refunded — the gate the trailing ↩
 * used before Refund moved into the detail. A voided gift is not a live gift;
 * a fully-refunded one (server `remainingRefundable === 0`) has nothing left to
 * refund and would only 400 at the backend cap. `null` means the ledger
 * didn't carry the cap → keep offering it (the backend cap stays the ultimate
 * guard). A filed/Reported gift still qualifies — it's the real-world refund
 * target, filed gross, with the refund booked as a separate row.
 */
function canRefundRow(
  item: TriageItem,
): item is Extract<TriageItem, { kind: 'Contribution' }> {
  if (item.kind !== 'Contribution') return false;
  if (isNotReportedResolution(item.resolvedAs)) return false;
  return (
    item.contribution.remainingRefundable == null ||
    item.contribution.remainingRefundable > 0
  );
}

/**
 * The Contribution row's Refund action — moved OUT of the trailing quick-action
 * gutter (where the bare ↩ went unrecognized, and a rare/heavy money move sat
 * next to the common reversible Exclude) INTO the row detail, labeled and
 * grouped with Delete. Opens the existing refund dialog straight on its refund
 * form, seeded with the row's full `ContributionDto` — so the refund POST
 * carries the gift's ACTUAL `concurrencyVersion` (never the hardcoded 0 that
 * 409s a touched row). The refund books a separate `ContributionRefund`; the
 * original gift is untouched (stays filed gross on a Reported row).
 */
function MoneyRowRefundAction({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={`triage-ledger-refund-${item.id}`}
        aria-label="Refund this contribution to the donor"
        title="Refund this contribution to the donor"
        onClick={() => setOpen(true)}
        className={cn(
          financeTextActionClass,
          'gap-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <Undo2 className="h-4 w-4" aria-hidden="true" />
        Refund
      </button>
      {open && (
        <RefundDialog
          contribution={item.contribution}
          open={open}
          onOpenChange={setOpen}
          refundFirst
        />
      )}
    </>
  );
}

/**
 * ADR-016 step 3 — the live money-row trailing quick-action: a one-click
 * Exclude that VOIDS the row (reversible — it lands in the "Not reported" home
 * and can be restored). Mirrors the bank ✕ Exclude; the heavy, permanent Delete
 * is NOT offered here — that's the deliberate second step inside the detail.
 */
function MoneyRowExcludeButton({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' | 'Expenditure' }>;
}) {
  const { errorMessage, run, errorId, isRunning } = useQuickAction(`${item.id}:action`);
  const { mutateAsync: voidContribution, isPending: voidingContribution } =
    useVoidContribution();
  const { mutateAsync: voidExpenditure, isPending: voidingExpenditure } =
    useVoidExpenditure();
  // `isRunning` (row-scoped, survives the optimistic void flip that swaps this
  // control for the Restore twin — both keyed `${item.id}:action`) keeps the pair
  // disabled through a rapid exclude/restore so no overlapping stale-version POST fires.
  const isPending = voidingContribution || voidingExpenditure || isRunning;
  // Named so both the click and the inline Retry send the version the row shows
  // now: after a 409 that is the stale one until the invalidate-on-settle refetch
  // lands, so a Retry before it 409s again rather than overwriting the other change.
  const exclude = () =>
    run(async () => {
      if (item.kind === 'Contribution') {
        await voidContribution({
          row: item.contribution,
        });
      } else {
        await voidExpenditure({
          row: item.expenditure,
        });
      }
    }, 'Failed to exclude this from the report.');
  return (
    <div className="flex flex-col items-end gap-0.5">
      <button
        type="button"
        data-testid={`triage-ledger-exclude-${item.id}`}
        aria-label="Exclude from report — keep it off the report (you can restore it later)"
        title="Exclude from report"
        disabled={isPending}
        onClick={exclude}
        className={cn(
          financeTextActionClass,
          // Labeled + neutral: the old bare ✕ (and its rose hover) read as
          // "delete". Exclude is reversible, so it stays a quiet muted action;
          // red is reserved for the permanent Delete in the detail.
          'gap-1.5 px-1.5 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <CircleMinus className="h-3.5 w-3.5" aria-hidden="true" />
        Exclude
      </button>
      <RowError id={errorId} message={errorMessage} onRetry={exclude} />
    </div>
  );
}

/**
 * ADR-016 step 3 — the voided money-row trailing quick-action: a one-click
 * Restore that UN-VOIDS the row (back to Pending for re-review; the treasurer
 * re-classifies its bank link). Mirrors the bank ↺ Restore.
 */
function MoneyRowRestoreButton({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' | 'Expenditure' }>;
}) {
  const { errorMessage, run, errorId, isRunning } = useQuickAction(`${item.id}:action`);
  const { mutateAsync: unvoidContribution, isPending: unvoidingContribution } =
    useUnvoidContribution();
  const { mutateAsync: unvoidExpenditure, isPending: unvoidingExpenditure } =
    useUnvoidExpenditure();
  // Row-scoped in-flight flag — the Exclude/Restore twins share `${item.id}:action`,
  // so this keeps the pair disabled through the optimistic swap (see MoneyRowExcludeButton).
  const isPending = unvoidingContribution || unvoidingExpenditure || isRunning;
  const restore = () =>
    run(async () => {
      if (item.kind === 'Contribution') {
        await unvoidContribution({
          row: item.contribution,
        });
      } else {
        await unvoidExpenditure({
          row: item.expenditure,
        });
      }
    }, 'Failed to restore this row.');
  return (
    <div className="flex flex-col items-end gap-0.5">
      <button
        type="button"
        data-testid={`triage-ledger-restore-${item.id}`}
        aria-label="Restore — bring this back for review"
        title="Restore"
        disabled={isPending}
        onClick={restore}
        className={cn(
          financeTextActionClass,
          'gap-1.5 px-1.5 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        Restore
      </button>
      <RowError id={errorId} message={errorMessage} onRetry={restore} />
    </div>
  );
}

function BankExcludeButton({
  item,
}: {
  item: Extract<TriageItem, { kind: 'BankLine' }>;
}) {
  const { errorMessage, run, errorId, isRunning } = useQuickAction(`${item.id}:action`);
  // scopeId queues this behind the line's classify, which is not a row write yet.
  const { mutateAsync: excludeAsync, isPending: excludePending } =
    useExcludeBankTransactionFromReport({ scopeId: item.id });
  // Row-scoped in-flight flag survives the optimistic exclude/restore swap (the
  // trailing ✕ and the Restore ↺ share `${item.id}:action`), so the pair stays
  // disabled through a rapid toggle and can't fire an overlapping stale POST.
  const isPending = excludePending || isRunning;
  // Quick exclude (no note) — the optional note lives in the panel's exclude
  // path. The hook invalidates the sort-inbox in onSettled. Named so Retry
  // re-reads the row's live version.
  const exclude = () =>
    run(async () => {
      await excludeAsync({
        row: item.bt,
      });
    }, 'Failed to exclude this bank line from the report.');
  return (
    <div className="flex flex-col items-end gap-0.5">
      <button
        type="button"
        data-testid={`triage-ledger-exclude-${item.id}`}
        aria-label="Exclude from report — not part of campaign reporting"
        title="Exclude from report"
        disabled={isPending}
        onClick={exclude}
        className={cn(
          financeTextActionClass,
          'gap-1.5 px-1.5 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <CircleMinus className="h-3.5 w-3.5" aria-hidden="true" />
        Exclude
      </button>
      <RowError id={errorId} message={errorMessage} onRetry={exclude} />
    </div>
  );
}

/**
 * Variant C (#575) — the inline, jurisdiction-aware amend guidance shown at the top of a boundary
 * straggler's opened detail. It reads the SERVED `amendmentModel` + `portalName` from
 * `usePortalUploadModel()` (never an `isAlabama` boolean / `FormatKind` re-derivation, AC5) and frames
 * the amend as the EXISTING classify → per-jurisdiction path (AC6): classify the line to route it into
 * its filed report's amendment, or use the exclude off-ramp below. No parallel amend flow.
 */
function StragglerAmendHint() {
  const portal = usePortalUploadModel();
  return (
    <div
      data-testid="straggler-amend-hint"
      className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] leading-snug text-amber-800"
    >
      <span className="font-semibold text-amber-900">Needs amendment. </span>
      {stragglerAmendHint(portal.amendmentModel, portal.portalName, portal.isEmailFiling)}
    </div>
  );
}

/**
 * The in-detail "Exclude from report" off-ramp for an opened NeedsAction bank
 * line (ADR-014). The detail's classification panel is mounted sign-based with
 * the type toggle hidden, so the exclude resolution has no home inside it — this
 * muted secondary button gives the treasurer the same one-click exclude as the
 * row-level trailing ✕ without collapsing the row (e.g. a Numero payout that's
 * really a transfer). Quick exclude (no note); the note lives in the panel's
 * exclude path used elsewhere. The hook invalidates the sort-inbox in onSettled;
 * we collapse the row on success, mirroring the panel's onClassified.
 */
function BankLineDetailExcludeAction({
  item,
  onCollapse,
}: {
  item: Extract<TriageItem, { kind: 'BankLine' }>;
  onCollapse: () => void;
}) {
  const { errorMessage, run, errorId, isRunning } = useQuickAction(`detail-${item.bt.id}`);
  // scopeId queues this behind the line's classify, which is not a row write yet.
  const { mutateAsync: excludeAsync, isPending: excludePending } =
    useExcludeBankTransactionFromReport({ scopeId: item.id });
  // Row-scoped in-flight flag — keeps this in-detail exclude disabled until settle.
  const isPending = excludePending || isRunning;
  const exclude = () =>
    run(async () => {
      await excludeAsync({
        row: item.bt,
      });
      onCollapse();
    }, 'Failed to exclude this bank line from the report.');
  return (
    <div className="flex flex-col items-end gap-0.5">
      <button
        type="button"
        data-testid={`triage-ledger-detail-exclude-${item.bt.id}`}
        aria-label="Exclude from report — not part of campaign reporting"
        title="Exclude from report"
        disabled={isPending}
        onClick={exclude}
        className={cn(financeTextActionClass, 'gap-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground')}
      >
        <CircleMinus className="h-4 w-4" aria-hidden="true" />
        Exclude from report
      </button>
      <RowError id={errorId} message={errorMessage} onRetry={exclude} />
    </div>
  );
}

function BankRestoreButton({
  item,
}: {
  item: Extract<TriageItem, { kind: 'BankLine' }>;
}) {
  const { errorMessage, run, errorId, isRunning } = useQuickAction(`${item.id}:action`);
  // scopeId queues the line's classify behind this reopen; classify is not a row write yet.
  const { mutateAsync: reopenAsync, isPending: reopenPending } =
    useReopenBankTransaction({ scopeId: item.id });
  // Row-scoped in-flight flag survives the optimistic exclude/restore swap.
  const isPending = reopenPending || isRunning;
  // ADR-014: every reopen is single-row (inter-account pairing retired).
  const restore = () =>
    run(async () => {
      try {
        await reopenAsync({
          row: item.bt,
        });
      } catch (err) {
        throw new Error(reopenErrorMessage(err));
      }
    }, 'Failed to restore this bank line.');
  return (
    <div className="flex flex-col items-end gap-0.5">
      <button
        type="button"
        data-testid={`triage-ledger-restore-${item.id}`}
        aria-label="Restore — reopen this bank line"
        title="Restore"
        disabled={isPending}
        onClick={restore}
        className={cn(
          financeTextActionClass,
          'gap-1.5 px-1.5 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        Restore
      </button>
      <RowError id={errorId} message={errorMessage} onRetry={restore} />
    </div>
  );
}

/**
 * Green ✓ + the resolution word (treasurer copy at the render boundary).
 * Uncheck is uniform: clicking the ✓ INSTANTLY moves the row one state back to
 * unprocessed — Confirmed contribution → Pending (unconfirm), Confirmed
 * expenditure → Pending (unconfirm), resolved bank row → Unattributed (reopen),
 * Linked refund → orphan (unlink), Standalone refund → orphan (revoke).
 * ADR-014: every bank-row reopen is single-row (inter-account pairing retired).
 */
function DoneActionCell({
  item,
}: {
  item: TriageItem;
}) {
  const { errorMessage, run, errorId, isRunning } = useQuickAction(item.id);
  const reducedMotion = useReducedMotion();
  const { mutateAsync: unconfirmAsync, isPending: unconfirmPending } =
    useUnconfirmContribution();
  const { mutateAsync: unconfirmExpenditureAsync, isPending: unconfirmExpPending } =
    useUnconfirmExpenditure();
  // scopeId queues a bank reopen ahead of the line's classify, and a refund unlink with its
  // revoke; neither classify nor the refund unchecks are row writes yet.
  const { mutateAsync: reopenAsync, isPending: reopenPending } =
    useReopenBankTransaction({ scopeId: item.id });
  const { mutateAsync: unlinkAsync, isPending: unlinkPending } =
    useUnlinkRefund({ scopeId: item.id });
  const { mutateAsync: revokeStandaloneAsync, isPending: revokePending } =
    useRevokeRefundStandalone({ scopeId: item.id });
  const isPending =
    unconfirmPending ||
    unconfirmExpPending ||
    reopenPending ||
    unlinkPending ||
    revokePending ||
    // Row-scoped in-flight flag: survives the optimistic control swap so a rapid
    // re-click of the uncheck twin can't fire an overlapping stale-version POST.
    isRunning;

  const uncheck = () => {
    switch (item.kind) {
      case 'Contribution':
        return run(
          () =>
            unconfirmAsync({
              row: item.contribution,
            }),
          'Failed to uncheck this contribution.',
        );
      case 'Expenditure':
        return run(async () => {
          try {
            await unconfirmExpenditureAsync({
              row: item.expenditure,
            });
          } catch (err) {
            throw new Error(unconfirmExpenditureErrorMessage(err));
          }
        }, 'Failed to uncheck this expenditure.');
      case 'BankLine': {
        return run(async () => {
          try {
            await reopenAsync({
              row: item.bt,
            });
          } catch (err) {
            throw new Error(reopenErrorMessage(err));
          }
        }, 'Failed to uncheck this bank transaction.');
      }
      case 'Refund':
        return run(
          () =>
            item.resolvedAs === 'Linked'
              ? unlinkAsync({
                  refundId: item.refund.id,
                  concurrencyVersion: item.refund.concurrencyVersion,
                })
              : revokeStandaloneAsync({
                  refundId: item.refund.id,
                  concurrencyVersion: item.refund.concurrencyVersion,
                }),
          'Failed to uncheck this refund.',
        );
      default:
        return assertNever(item);
    }
  };

  // Symbols only in the status column — a bold, filled green check; the
  // resolution word (Confirmed / Transfer / Linked to original / …) lives in
  // the accessible label + hover title, never as visible text beside it.
  const label = resolvedAsLabel(item.resolvedAs) ?? 'Done';
  // §34 — a Confirmed money row is in the treasurer's records but not yet in the
  // export, so for append-only portal-upload jurisdictions (AL today) it hasn't
  // reached the portal. Only the Confirmed resolution carries this; refunds /
  // deposits mean something else. §18 — a resolution-less Done row (a confirmed
  // expenditure, whose label falls back to the bare word "Done") gets its own
  // "reconciled and ready" line so "Done" never reads as "filed with the state".
  // Both are omitted for federal in-system e-file.
  const portal = usePortalUploadModel();
  const detail =
    portal.isPortalUpload && portal.stateDisplayName
      ? item.resolvedAs === 'Confirmed'
        ? `Confirmed in your records here — not yet sent to the ${portal.stateDisplayName} ${portal.portalName}.`
        : item.resolvedAs === null
          ? `Reconciled and ready — not yet sent to the ${portal.stateDisplayName} ${portal.portalName}.`
          : undefined
      : undefined;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <StatusTooltip label={label} detail={detail}>
        <button
          type="button"
          data-testid={`triage-ledger-done-${item.id}`}
          aria-label={`${label} — uncheck`}
          disabled={isPending}
          onClick={uncheck}
          // 44px tap target; the green check icon stays 24px inside it. On a
          // rejected uncheck the check snaps back with a brief motion (unless reduced).
          className={cn(
            'inline-flex h-11 w-11 items-center justify-center rounded-full text-emerald-600 hover:text-emerald-700 disabled:opacity-50',
            errorMessage && !reducedMotion && 'animate-ledger-revert',
          )}
        >
          <CheckCircle2
            className="h-6 w-6 fill-emerald-600 stroke-white"
            aria-hidden="true"
          />
        </button>
      </StatusTooltip>
      <RowError id={errorId} message={errorMessage} onRetry={uncheck} />
    </div>
  );
}

/**
 * Lock symbol — inert; mirrors the backend's refusal to edit a filed row.
 * Symbols only: the "Reported" word shows on hover (tooltip) + the accessible
 * label, never as visible text.
 */
function ReportedActionCell({ id, detail }: { id: string; detail?: string }) {
  // §34 — "Reported" means the row is on file (stamped by a filing action, #1007). For
  // portal-upload jurisdictions the hover names where it was filed (the §33 legend note),
  // unless the caller has a more specific note (a reopened period).
  const portal = usePortalUploadModel();
  const reportedDetail = detail ?? reportedOnFileNote(portal);
  return (
    <StatusTooltip label="Reported" detail={reportedDetail}>
      <span
        data-testid={`triage-ledger-reported-${id}`}
        aria-label="Reported"
        className="inline-flex h-6 w-6 items-center justify-center text-muted-foreground"
      >
        <Lock className="h-4 w-4" aria-hidden="true" />
      </span>
    </StatusTooltip>
  );
}

const noopRegister = () => {};

/**
 * ADR-016 step 1 — the read-only detail shown when any row is expanded
 * (click-into-the-row). Renders the transaction's key fields for every
 * kind/status, so a confirmed / reported / excluded / bank row can be drilled
 * into regardless of whether it still needs action. The permanent-Delete
 * action lands inside this detail in step 2.
 */
function RowDetailSummary({ item }: { item: TriageItem }) {
  const { data: profile } = useComplianceProfile();
  const offersEventDates = profile?.offersExpenditureEventDates === true;
  return (
    <dl
      data-testid={`triage-ledger-detail-${item.id}`}
      className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm"
    >
      {detailFields(item, offersEventDates).map(({ label, value }) => (
        <Fragment key={label}>
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="font-medium">{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/**
 * ADR-016 step 2 — permanent Delete, inside the row detail. The quick trailing
 * action only ever excludes/restores (the reversible step); Delete is the
 * deliberate SECOND step — reached by opening the row and gated by a heavy
 * "permanent, cannot be undone" confirm. Offered on a non-Reported money row;
 * a Reported row is locked (amend the report instead), and bank-line delete
 * lands in its own slice (it needs a dedicated endpoint).
 */
function RowDeleteAction({ item }: { item: TriageItem }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<DeleteRefusal | null>(null);

  const { mutateAsync: deleteContribution, isPending: deletingContribution } =
    useDeleteContribution();
  const { mutateAsync: deleteExpenditure, isPending: deletingExpenditure } =
    useDeleteExpenditure();
  const { mutateAsync: deleteRefund, isPending: deletingRefund } =
    useDeleteContributionRefund();
  const { mutateAsync: deleteBankTransaction, isPending: deletingBank } =
    useDeleteBankTransaction();
  const isPending =
    deletingContribution ||
    deletingExpenditure ||
    deletingRefund ||
    deletingBank;

  if (!canDeleteRow(item)) return null;

  const doDelete = async () => {
    setError(null);
    try {
      switch (item.kind) {
        case 'Contribution':
          await deleteContribution({
            row: item.contribution,
          });
          break;
        case 'Expenditure':
          await deleteExpenditure({
            row: item.expenditure,
          });
          break;
        case 'Refund':
          await deleteRefund({
            refundId: item.refund.id,
            concurrencyVersion: item.refund.concurrencyVersion,
          });
          break;
        case 'BankLine':
          await deleteBankTransaction({
            id: item.bt.id,
            concurrencyVersion: item.bt.concurrencyVersion,
          });
          break;
      }
      setOpen(false);
    } catch (err) {
      setError(detailDeleteRefusal(err));
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`triage-ledger-detail-delete-${item.id}`}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
        className={cn(financeTextActionClass, 'gap-1.5 text-rose-700 hover:bg-rose-50 hover:underline')}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
        {PreflightCopy.DetailDeleteButton}
      </button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) setError(null);
          setOpen(next);
        }}
      >
        <DialogContent
          className="max-w-md"
          data-testid={`triage-ledger-detail-delete-dialog-${item.id}`}
        >
          <DialogHeader>
            <DialogTitle>{PreflightCopy.DetailDeleteTitle}</DialogTitle>
            <DialogDescription>
              {PreflightCopy.ChooserDeleteOptionBody}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm font-medium text-rose-700">
            {PreflightCopy.ChooserDeletePermanentLine}
          </p>
          {item.kind === 'BankLine' && (
            <p className="text-xs text-muted-foreground">
              {PreflightCopy.DetailDeleteBankReimportNotice}
            </p>
          )}
          {error && (
            <div
              role="alert"
              data-testid={`triage-ledger-detail-delete-error-${item.id}`}
              className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
            >
              {error.message}
              {error.filedReport && (
                <FiledReportAmendLink
                  filedReport={error.filedReport}
                  testId={`triage-ledger-detail-filed-report-link-${item.id}`}
                />
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isPending}
              onClick={() => setOpen(false)}
            >
              Keep it
            </Button>
            <PendingButton
              type="button"
              size="sm"
              data-testid={`triage-ledger-detail-delete-confirm-${item.id}`}
              pending={isPending}
              onClick={doDelete}
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              {PreflightCopy.DetailDeleteButton}
            </PendingButton>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * The row-detail action footer — the deliberate money moves that are pointedly
 * NOT one-click trailing quick-actions. Refund (rare, moves real money, books a
 * separate row) and the permanent Delete both live here, reached only by opening
 * the row, so neither is mistaken for the reversible trailing Exclude. Renders
 * nothing when neither applies, so a row with no heavy action shows no empty
 * bordered footer.
 */
function RowDetailActions({ item }: { item: TriageItem }) {
  const canRefund = canRefundRow(item);
  const canDelete = canDeleteRow(item);
  if (!canRefund && !canDelete) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t pt-3">
      {canRefundRow(item) && <MoneyRowRefundAction item={item} />}
      {canDelete && <RowDeleteAction item={item} />}
    </div>
  );
}

/**
 * Translates a detail-delete mutation failure to treasurer copy at the render
 * boundary — the specific delete-refusal codes first, then the calendar-lock
 * refusal's server reason, then a generic 409 to the refresh prompt. No raw backend
 * code ever reaches the dialog.
 */
function detailDeleteRefusal(err: unknown): DeleteRefusal {
  // ADR-081 first — the entry is on a report already sent to the regulator. It carries the
  // report to link to, so it must not fall through to the generic 409 refresh prompt (which
  // would tell the treasurer to retry something that will never succeed).
  const onFiledReport = filedReportRefusal(err);
  if (onFiledReport) return onFiledReport;

  if (err instanceof StagedRowUpdateError) {
    if (err.code === ErrorCode.DeleteHasDependentRefunds) {
      return plainRefusal(PreflightCopy.ChooserDependentRefundsError);
    }
    if (err.code === ErrorCode.DeleteReferencedByFiling) {
      return plainRefusal(PreflightCopy.ChooserReferencedByFilingError);
    }
    // The calendar lock with no in-app report to name (a filed one refuses with
    // SupportsFiledReport above), such as history reported before onboarding. There is no
    // report to link, so relay the server's reason.
    if (err.code === ErrorCode.StagedRowUpdateAlreadyReported) {
      return plainRefusal(err.message);
    }
    // A classified bank line can't be deleted directly — surface the backend's
    // plain-English reason (already render-safe), not the generic refresh prompt
    // the 409 fallthrough would otherwise pick.
    if (err.code === ErrorCode.DeleteBankLineClassified) {
      return plainRefusal(err.message);
    }
    if (err.status === 409 || isStaleWrite(err)) {
      return plainRefusal(PreflightCopy.ChooserConcurrencyError);
    }
    return plainRefusal(err.message);
  }
  return plainRefusal(err instanceof Error ? err.message : 'Something went wrong. Try again.');
}

// ──────────────────────────────────────────────────────────────────
// ADR-016 (2026-06-25): per-field editing relocated out of the dense
// register row and INTO the row detail. These editor field components
// hold the SAME click-to-edit controls (identical testids) and the SAME
// `onSave` mutation bodies that used to live in the register cells. The
// register cells (DateCell / AmountCell / DescriptionCell /
// ExpenditureCategoryCell) are now display-only.
//
// Editability guards are preserved verbatim:
//  - Contribution / Expenditure: editable only when the served `freeze` allows
//    it (`moneyRowAffordances`); a frozen row, voided ones included, is plain text.
//  - Refund: editable unless `status === 'Reported'`.
//  - BankLine: editable only when `status === 'NeedsAction'` (the PATCH refuses
//    non-Unattributed rows); date/account are always display-only.
//  - Expenditure amount is NOT editable (parity with the prior register).
// ──────────────────────────────────────────────────────────────────

type ContributionSlim = Extract<
  TriageItem,
  { kind: 'Contribution' }
>['contribution'];
type UpdateContributionFn = ReturnType<
  typeof useUpdateContribution
>['mutateAsync'];
type QueryClientType = ReturnType<typeof useQueryClient>;

/**
 * Saves a single-field contribution edit from the row detail, then invalidates the
 * sort inbox to refetch the row. `useUpdateContribution` re-sends every field the
 * edit leaves alone as the row has it when the save runs.
 */
async function saveContributionField(
  c: ContributionSlim,
  updateContribution: UpdateContributionFn,
  queryClient: QueryClientType,
  patch: {
    date?: string;
    amount?: number;
    contributionType?: ReceiptNature;
    transferQualification?: FederalTransferQualification | null;
    inKindNature?: InKindNature | null;
    inKindDescription?: string | null;
    inKindFairMarketValueBasis?: string | null;
  },
) {
  await updateContribution({ row: c, ...patch });
  queryClient.invalidateQueries({
    queryKey: queryKeys.finance.sortInboxAll(),
  });
}

function ContributionDateField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: updateContribution } = useUpdateContribution();
  const c = item.contribution;
  if (!moneyRowAffordances(item.freeze).edit) return <>{formatDate(c.date)}</>;
  return (
    <InlineEditCell
      id={item.id}
      field="date"
      editLabel="date"
      value={c.date}
      display={formatDate(c.date)}
      onSave={async (next) => {
        await saveContributionField(c, updateContribution, queryClient, {
          date: next,
        });
      }}
    />
  );
}

function ContributionAmountField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: updateContribution } = useUpdateContribution();
  const c = item.contribution;
  if (!moneyRowAffordances(item.freeze).edit) return <>{formatAmount(c.amount)}</>;
  return (
    <InlineEditCell
      id={item.id}
      field="amount"
      editLabel="amount"
      value={String(c.amount)}
      display={formatAmount(c.amount)}
      onSave={async (next) => {
        await saveContributionField(c, updateContribution, queryClient, {
          amount: parseAmountDraft(next),
        });
      }}
    />
  );
}

/**
 * The editable designation (contributionType) picker in the expanded
 * contribution detail — lets a treasurer FIX a mis-imported type (a PAC that
 * imported as an individual, an in-kind that landed as cash). A shadcn Select
 * whose options are JURISDICTION-AWARE: the shared
 * `contributionDesignationOptions(profile)` returns the Alabama FCPA portal
 * source list (adds Other) vs the neutral Cash/In-Kind pair, plus Federal Transfer
 * only where the jurisdiction declares it (`offersFederalTransferReceipt`).
 * Changing it saves through `saveContributionField`. Locked (plain-text label) on a
 * Reported row, mirroring how the date/amount editors lock.
 */
function ContributionDesignationField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
}) {
  const queryClient = useQueryClient();
  // `isPending` drives the inline spinner: a type change is a non-optimistic HTTP
  // round-trip, so without a signal the treasurer can't tell the pick registered.
  const { mutateAsync: updateContribution, isPending: isSaving } =
    useUpdateContribution();
  const { data: settings } = useFinanceSettings();
  const { data: profile } = useComplianceProfile();
  const [error, setError] = useState<string | null>(null);
  const c = item.contribution;
  const current = c.contributionType;
  const rulesetCode = settings?.rulesetCode ?? null;
  // The picker is jurisdiction-aware: Alabama's FCPA portal has a different source
  // list, and Federal Transfer is federal-only. The shared helper reads the served
  // profile (not a bare ruleset code) so a loaded state profile (VA/NC/LA) never
  // sees the federal-only Federal Transfer receipt — it is the one source of truth.
  const options = contributionDesignationOptions(profile);

  // In-Kind can't save-on-select: the update path hard-requires a description + FMV
  // basis (and a nature on Alabama), so picking it STAGES a draft and reveals the
  // capture rather than committing. "Save in-kind details" commits once the draft is
  // complete for this jurisdiction; any other type still saves immediately. The draft
  // seeds from the row's existing in-kind values so editing an in-kind row pre-fills.
  const [pendingInKind, setPendingInKind] = useState(false);
  const [draft, setDraft] = useState(() => ({
    nature: c.inKindNature ?? null,
    description: c.inKindDescription ?? '',
    // On Virginia the basis is a closed dropdown; a legacy off-set free-text value seeds
    // BLANK so the treasurer must re-pick a member that files faithfully (the
    // reconciliation). Everywhere else it seeds the stored free text verbatim.
    fairMarketValueBasis:
      requiresInKindValuationBasis(rulesetCode)
      && !isClosedInKindValuationBasis(c.inKindFairMarketValueBasis)
        ? ''
        : c.inKindFairMarketValueBasis ?? '',
  }));

  if (!moneyRowAffordances(item.freeze).edit) return <>{contributionTypeLabel(current)}</>;

  const effectiveType: ReceiptNature = pendingInKind ? ReceiptNature.InKind : current;
  const showInKindCapture = effectiveType === ReceiptNature.InKind;
  const natureRequired = requiresInKindNature(rulesetCode);
  // Virginia's COMET Schedule B accepts only a closed valuation-basis set, so VA renders a
  // dropdown constrained to those members instead of the free-text input other jurisdictions
  // use — the "reconcile free-text vs closed-enum" fix, scoped to the VA-facing input.
  const basisIsClosedSet = requiresInKindValuationBasis(rulesetCode);
  const captureComplete = isInKindCaptureComplete(rulesetCode, draft);

  const toError = (err: unknown) =>
    isStaleWrite(err)
      ? REFRESH_PROMPT
      : err instanceof Error
        ? err.message
        : 'Failed to change the designation.';

  const handleChange = async (next: string) => {
    const nextType = next as ReceiptNature;
    if (nextType === effectiveType) return;
    setError(null);
    if (nextType === ReceiptNature.InKind) {
      // Reveal the capture; commit happens on Save once the draft is complete.
      setPendingInKind(true);
      return;
    }
    // A non-in-kind type saves immediately — the server clears any in-kind capture.
    setPendingInKind(false);
    try {
      await saveContributionField(c, updateContribution, queryClient, {
        contributionType: nextType,
      });
    } catch (err) {
      setError(toError(err));
    }
  };

  const handleSaveInKind = async () => {
    setError(null);
    try {
      await saveContributionField(c, updateContribution, queryClient, {
        contributionType: ReceiptNature.InKind,
        // Nature is AL-only; elsewhere it stays null (the filing ignores it).
        inKindNature: natureRequired ? draft.nature : null,
        inKindDescription: draft.description.trim(),
        inKindFairMarketValueBasis: draft.fairMarketValueBasis.trim(),
      });
      setPendingInKind(false);
    } catch (err) {
      setError(toError(err));
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Select
          value={effectiveType}
          onValueChange={(v) => void handleChange(v)}
          disabled={isSaving}
        >
          <SelectTrigger
            data-testid={`triage-ledger-designation-select-${item.id}`}
            aria-label="Transaction type"
            className="h-9 w-48"
          >
            {/* ADR-057 (#344) — a historical row whose stored designation is a RETIRED source
                (e.g. "Individual") is no longer among the jurisdiction's options, so Radix has no
                item label to show. Fall back to the friendly label of the current value so the
                trigger never renders blank; picking any live option replaces it. */}
            <SelectValue placeholder={contributionTypeLabel(effectiveType)} />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isSaving && (
          <InlineSpinner
            testId={`triage-ledger-designation-saving-${item.id}`}
            label="Saving"
          />
        )}
      </div>

      {/* #711 — a federal transfer's exemption is not free: it rests on the money having come
          from another committee authorized by the same candidate. Say which way this row was
          answered and what follows from it, on the row itself, so the treasurer never has to
          infer why one transfer metered and another did not. */}
      {effectiveType === ReceiptNature.FederalTransfer
        && profile?.offersFederalTransferReceipt === true && (
        <div className="flex flex-col gap-1">
          <label
            htmlFor={`triage-ledger-transfer-qualification-${item.id}`}
            className="text-xs font-medium"
          >
            What makes this transfer exempt?
          </label>
          <select
            id={`triage-ledger-transfer-qualification-${item.id}`}
            data-testid={`triage-ledger-transfer-qualification-${item.id}`}
            className="h-9 w-72 rounded-md border px-2 text-sm"
            value={c.transferQualification ?? ''}
            disabled={isSaving}
            onChange={(e) => {
              void (async () => {
                setError(null);
                try {
                  await saveContributionField(c, updateContribution, queryClient, {
                    transferQualification: e.target.value as FederalTransferQualification,
                  });
                } catch (err) {
                  setError(toError(err));
                }
              })();
            }}
          >
            {/* A row that predates the gate has no answer yet; it reads as unanswered rather than
                silently as "None", because only one of those still carries the exemption. */}
            {c.transferQualification == null && <option value="">Select…</option>}
            {FEDERAL_TRANSFER_QUALIFICATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            {transferQualificationSummary(c.transferQualification)}
          </p>
        </div>
      )}

      {/* In-kind capture — revealed when the (pending or persisted) type is In-Kind.
          Description + FMV basis are required on every jurisdiction; the nature picker
          only shows where the jurisdiction requires it (Alabama), and the FMV basis is a
          closed dropdown on Virginia (COMET Schedule B) vs free text elsewhere. Save is
          gated on the shared completeness predicate so a treasurer never trips the raw
          server rule. */}
      {showInKindCapture && (
        <div
          className="flex flex-col gap-2"
          data-testid={`triage-ledger-inkind-capture-${item.id}`}
        >
          {natureRequired && (
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">Nature of the in-kind gift</span>
              <Select
                value={draft.nature ?? ''}
                onValueChange={(v) =>
                  setDraft((d) => ({ ...d, nature: v as InKindNature }))
                }
              >
                <SelectTrigger
                  data-testid={`triage-ledger-inkind-nature-${item.id}`}
                  aria-label="Nature of the in-kind gift"
                  className="h-9 w-48"
                >
                  <SelectValue placeholder="Choose a nature…" />
                </SelectTrigger>
                <SelectContent>
                  {IN_KIND_NATURE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}

          {natureRequired && draft.nature === InKindNature.Other && (
            <p
              className="text-xs text-muted-foreground"
              data-testid={`triage-ledger-inkind-other-hint-${item.id}`}
            >
              You&apos;ll file this one directly in the state portal — it&apos;s left
              off the electronic file.
            </p>
          )}

          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">What was given</span>
            <Input
              data-testid={`triage-ledger-inkind-description-${item.id}`}
              aria-label="What was given"
              className="h-9 w-64"
              value={draft.description}
              onChange={(e) =>
                setDraft((d) => ({ ...d, description: e.target.value }))
              }
              placeholder="e.g. Printed yard signs"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">How the value was set</span>
            {basisIsClosedSet ? (
              // Virginia files this to COMET's closed valuationBasisType, so the treasurer
              // picks from the exact two members — the stored string round-trips to the
              // file with no silent coercion.
              <Select
                value={draft.fairMarketValueBasis}
                onValueChange={(v) =>
                  setDraft((d) => ({ ...d, fairMarketValueBasis: v }))
                }
              >
                <SelectTrigger
                  data-testid={`triage-ledger-inkind-fmv-select-${item.id}`}
                  aria-label="How the value was set"
                  className="h-9 w-64"
                >
                  <SelectValue placeholder="Choose how the value was set…" />
                </SelectTrigger>
                <SelectContent>
                  {IN_KIND_VALUATION_BASIS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                data-testid={`triage-ledger-inkind-fmv-${item.id}`}
                aria-label="How the value was set"
                className="h-9 w-64"
                value={draft.fairMarketValueBasis}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, fairMarketValueBasis: e.target.value }))
                }
                placeholder="e.g. Vendor invoice or catalog price"
              />
            )}
          </label>

          <div>
            <Button
              type="button"
              size="sm"
              data-testid={`triage-ledger-inkind-save-${item.id}`}
              disabled={!captureComplete}
              onClick={() => void handleSaveInKind()}
            >
              Save in-kind details
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p
          role="alert"
          data-testid={`triage-ledger-designation-error-${item.id}`}
          className="text-xs text-rose-700"
        >
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The editable contributor-name field in the expanded contribution detail. Shows
 * the composed display name with a pencil affordance; click opens the inline
 * name editor. Locked (plain text) on a Reported row, mirroring the other
 * detail editors.
 */
function ContributionContributorField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
}) {
  const [editing, setEditing] = useState(false);
  const c = item.contribution;

  if (!moneyRowAffordances(item.freeze).edit) return <>{c.contactName ?? '—'}</>;

  if (editing) {
    return (
      <ContributionNameEditor
        item={item}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <button
      type="button"
      data-testid={`triage-ledger-contributor-button-${item.id}`}
      className={financeInlineEditTriggerClass}
      aria-label="Edit contributor"
      title="Edit contributor"
      onClick={() => setEditing(true)}
    >
      {c.contactName ?? '—'}
      <Pencil className="h-3 w-3 opacity-60" aria-hidden="true" />
    </button>
  );
}

/**
 * The inline contributor-name editor: edit a contributor's name and switch the
 * underlying `FinanceContact` between a person (first/last) and an organization
 * (single name) — the real case being a PAC that imported as a person and must
 * become the organization. Saves via the contact PUT
 * (`useUpdateFinanceContact`), sending a COHERENT single shape (person →
 * first/last, no org; organization → org name + Organization, no person parts) —
 * the backend wipes the other shape's fields before the per-row CHECK and rejects
 * an incoherent mix, so we send only the target shape. The contact's real version
 * (`contactConcurrencyVersion`) rides the optimistic lock, never a hardcoded 0.
 */
function ContributionNameEditor({
  item,
  onDone,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' }>;
  onDone: () => void;
}) {
  const c = item.contribution;
  const { mutateAsync: updateContact, isPending } = useUpdateFinanceContact();
  // Prefill kind + parts from the row's contact fields (surfaced on the DTO).
  const initialKind = c.contactKind ?? ContactKind.Individual;
  const [kind, setKind] = useState<ContactKind>(initialKind);
  const [firstName, setFirstName] = useState(c.contactFirstName ?? '');
  const [lastName, setLastName] = useState(c.contactLastName ?? '');
  // Occupation + employer ride the same person edit — required for itemized
  // individual contributions (FEC best-efforts, AL itemization). Prefilled from
  // the contact fields surfaced on the DTO; individual-only (orgs carry neither).
  const [occupation, setOccupation] = useState(c.contactOccupation ?? '');
  const [employer, setEmployer] = useState(c.contactEmployer ?? '');
  // Employment location (VA COMET city/state of employment) rides the same
  // person edit as employer. Optional at the input level (never gates Save).
  // Prefilled from the row DTO so a name edit doesn't clobber an existing value.
  const [employmentLocation, setEmploymentLocation] = useState(c.contactEmploymentLocation ?? '');
  // Prefill the org name from the org part when present, else the composed
  // display name (so an individual→organization switch starts from the name the
  // treasurer already sees, ready to clean up).
  const [orgName, setOrgName] = useState(
    c.contactOrganizationName ?? c.contactName ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  const isOrg = kind === ContactKind.Organization;
  const canSave = isOrg
    ? orgName.trim() !== ''
    : firstName.trim() !== '' && lastName.trim() !== '';

  const handleSave = async () => {
    setError(null);
    try {
      await updateContact({
        id: c.contactId,
        // Coherent single shape — the OTHER shape's parts go null so the backend
        // per-row CHECK (Individual → first+last, no org; Organization → org, no
        // person parts) passes. The name interceptor recomposes the display name.
        data: isOrg
          ? updateFinanceContactRequest({
              contactKind: ContactKind.Organization,
              organizationName: orgName.trim(),
              firstName: null,
              middleName: null,
              lastName: null,
              nameSuffix: null,
              concurrencyVersion: c.contactConcurrencyVersion,
            })
          : updateFinanceContactRequest({
              contactKind: ContactKind.Individual,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              organizationName: null,
              // Best-efforts fields, never gated by `canSave`. The contact PUT
              // reads null as "keep", so a blanked field goes as '' to clear it.
              occupation: occupation.trim(),
              employer: employer.trim(),
              employmentLocation: employmentLocation.trim(),
              concurrencyVersion: c.contactConcurrencyVersion,
            }),
      });
      onDone();
    } catch (err) {
      setError(
        err instanceof FinanceContactUpdateError && isStaleWrite(err)
          ? REFRESH_PROMPT
          : err instanceof Error
            ? err.message
            : 'Failed to update the contributor.',
      );
    }
  };

  return (
    <div
      data-testid={`triage-ledger-contributor-editor-${item.id}`}
      className="flex flex-col gap-2"
    >
      {/* Person vs. organization toggle — drives which inputs show. */}
      <ToggleGroup
        type="single"
        variant="outline"
        aria-label="Contributor kind"
        value={kind}
        onValueChange={(v) => {
          if (!v) return;
          const next = v as ContactKind;
          if (next === kind) return;
          // Carry the name ACROSS the switch so it never reads as wiped. An org
          // has no first/last, so a naive toggle to Person would blank the name
          // and force a retype; split the org name (first token → first, rest →
          // last) as a best-effort the treasurer can refine. Switching to an org
          // with no org name yet composes it from the person's first/last.
          if (next === ContactKind.Individual) {
            if (firstName.trim() === '' && lastName.trim() === '') {
              const tokens = orgName.trim().split(/\s+/).filter(Boolean);
              if (tokens.length > 0) {
                setFirstName(tokens[0]);
                setLastName(tokens.slice(1).join(' '));
              }
            }
          } else if (orgName.trim() === '') {
            const composed = [firstName, lastName]
              .map((s) => s.trim())
              .filter(Boolean)
              .join(' ');
            if (composed) setOrgName(composed);
          }
          setKind(next);
        }}
      >
        <ToggleGroupItem
          value={ContactKind.Individual}
          data-testid={`triage-ledger-contributor-kind-person-${item.id}`}
        >
          Person
        </ToggleGroupItem>
        <ToggleGroupItem
          value={ContactKind.Organization}
          data-testid={`triage-ledger-contributor-kind-org-${item.id}`}
        >
          Organization
        </ToggleGroupItem>
      </ToggleGroup>

      {isOrg ? (
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Organization name</span>
          <input
            data-testid={`triage-ledger-contributor-org-${item.id}`}
            type="text"
            value={orgName}
            disabled={isPending}
            onChange={(e) => setOrgName(e.target.value)}
            className={financeFormInputClass}
          />
        </label>
      ) : (
        <div className="flex flex-wrap gap-2">
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="font-medium">First name</span>
            <input
              data-testid={`triage-ledger-contributor-first-${item.id}`}
              type="text"
              value={firstName}
              disabled={isPending}
              onChange={(e) => setFirstName(e.target.value)}
              className={financeFormInputClass}
            />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="font-medium">Last name</span>
            <input
              data-testid={`triage-ledger-contributor-last-${item.id}`}
              type="text"
              value={lastName}
              disabled={isPending}
              onChange={(e) => setLastName(e.target.value)}
              className={financeFormInputClass}
            />
          </label>
        </div>
      )}

      {/* Occupation + employer — individual-only (an organization carries
          neither). Required for itemized individual contributions: the FEC
          best-efforts obligation for donors over the itemization threshold and
          AL's per-payee itemization both read these. Optional at the input
          level (never gates Save); the preflight nudges when they're missing. */}
      {!isOrg && (
        <div className="flex flex-wrap gap-2">
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="font-medium">Occupation</span>
            <input
              data-testid={`triage-ledger-contributor-occupation-${item.id}`}
              type="text"
              value={occupation}
              disabled={isPending}
              onChange={(e) => setOccupation(e.target.value)}
              className={financeFormInputClass}
            />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="font-medium">Employer</span>
            <input
              data-testid={`triage-ledger-contributor-employer-${item.id}`}
              type="text"
              value={employer}
              disabled={isPending}
              onChange={(e) => setEmployer(e.target.value)}
              className={financeFormInputClass}
            />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="font-medium">Employment location</span>
            <input
              data-testid={`triage-ledger-contributor-employment-location-${item.id}`}
              type="text"
              value={employmentLocation}
              disabled={isPending}
              onChange={(e) => setEmploymentLocation(e.target.value)}
              placeholder="City, State"
              className={financeFormInputClass}
            />
          </label>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          data-testid={`triage-ledger-contributor-save-${item.id}`}
          disabled={!canSave || isPending}
          onClick={() => void handleSave()}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="ghost"
          data-testid={`triage-ledger-contributor-cancel-${item.id}`}
          disabled={isPending}
          onClick={onDone}
        >
          Cancel
        </Button>
      </div>
      {error && (
        <p
          role="alert"
          data-testid={`triage-ledger-contributor-error-${item.id}`}
          className="text-xs text-rose-700"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function ExpenditureDateField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
}) {
  const { mutateAsync: updateExpenditure } = useUpdateExpenditure();
  const e = item.expenditure;
  if (!moneyRowAffordances(item.freeze).edit) return <>{formatDate(e.date)}</>;
  return (
    <InlineEditCell
      id={item.id}
      field="date"
      editLabel="date"
      value={e.date}
      display={formatDate(e.date)}
      onSave={async (next) => {
        await updateExpenditure({ row: e, date: next });
      }}
    />
  );
}

function ExpenditureEventDateField({
  item,
  which,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
  which: 'authorized' | 'incurred';
}) {
  const { mutateAsync: updateExpenditure } = useUpdateExpenditure();
  const e = item.expenditure;
  const value = which === 'authorized' ? e.authorizedDate : e.incurredDate;
  const display = value ? formatDate(value) : '—';
  if (!moneyRowAffordances(item.freeze).edit) return <>{display}</>;
  return (
    <InlineEditCell
      id={`${item.id}-${which}`}
      field="date"
      editLabel={`${which} date`}
      value={value ?? ''}
      display={display}
      onSave={async (next) => {
        const date = next === '' ? null : next;
        await updateExpenditure({
          row: e,
          updateEventDates: true,
          ...(which === 'authorized' ? { authorizedDate: date } : { incurredDate: date }),
        });
      }}
    />
  );
}

function RefundDateField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Refund' }>;
}) {
  const { mutateAsync: editRefund } = useEditContributionRefund();
  const r = item.refund;
  if (item.status === 'Reported') return <>{formatDate(r.refundedAt)}</>;
  return (
    <InlineEditCell
      id={item.id}
      field="date"
      editLabel="date"
      value={r.refundedAt}
      display={formatDate(r.refundedAt)}
      onSave={async (next) => {
        await editRefund({
          refundId: r.id,
          data: { concurrencyVersion: r.concurrencyVersion, date: next },
        });
      }}
    />
  );
}

function RefundReasonField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Refund' }>;
}) {
  const { mutateAsync: editRefund } = useEditContributionRefund();
  const r = item.refund;
  if (item.status === 'Reported') return <>{r.reason || '—'}</>;
  return (
    <InlineEditCell
      id={item.id}
      field="desc"
      editLabel="reason"
      value={r.reason}
      display={r.reason || '—'}
      onSave={async (next) => {
        await editRefund({
          refundId: r.id,
          data: { concurrencyVersion: r.concurrencyVersion, reason: next },
        });
      }}
    />
  );
}

function RefundAmountField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Refund' }>;
}) {
  const { mutateAsync: editRefund } = useEditContributionRefund();
  const r = item.refund;
  if (item.status === 'Reported') return <>{formatAmount(r.amount)}</>;
  return (
    <InlineEditCell
      id={item.id}
      field="amount"
      editLabel="amount"
      value={String(r.amount)}
      display={formatAmount(r.amount)}
      onSave={async (next) => {
        await editRefund({
          refundId: r.id,
          data: {
            concurrencyVersion: r.concurrencyVersion,
            amount: parseAmountDraft(next),
          },
        });
      }}
    />
  );
}

/** The editable Expenditure payee (vendor picker) + purpose, for the detail. */
function ExpenditurePayeePurposeField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
}) {
  const { mutateAsync: updateExpenditure } = useUpdateExpenditure();
  const e = item.expenditure;
  if (!moneyRowAffordances(item.freeze).edit) {
    return (
      <span>
        {e.payeeName}
        {e.purpose && (
          <span className="text-muted-foreground">
            {' — '}
            {e.purpose}
          </span>
        )}
      </span>
    );
  }
  return (
    <ExpenditurePayeePurposeEdit
      id={item.id}
      expenditure={e}
      updateExpenditure={updateExpenditure}
    />
  );
}

/** The editable Expenditure category dropdown, for the detail. */
function ExpenditureCategoryField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
}) {
  // Root cause #3 — the category re-pick.
  const { mutateAsync: updateExpenditure } = useUpdateExpenditure();
  const { data: settings } = useFinanceSettings();
  if (!moneyRowAffordances(item.freeze).edit) {
    return <>{expenditureCategoryLabel(item.expenditure.category, settings?.rulesetCode)}</>;
  }
  return (
    <ExpenditureCategoryEdit
      id={item.id}
      expenditure={item.expenditure}
      updateExpenditure={updateExpenditure}
    />
  );
}

/**
 * The editable Expenditure amount, for the detail. A treasurer can correct the
 * figure — overriding a bank-matched amount is allowed on purpose (they know the
 * statement). A frozen row is plain text,
 * mirroring the other detail editors.
 */
function ExpenditureAmountField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
}) {
  const { mutateAsync: updateExpenditure } = useUpdateExpenditure();
  const e = item.expenditure;
  if (!moneyRowAffordances(item.freeze).edit) {
    return <span className="font-mono">{formatAmount(e.amount)}</span>;
  }
  return (
    <InlineEditCell
      id={item.id}
      field="amount"
      editLabel="amount"
      value={String(e.amount)}
      display={formatAmount(e.amount)}
      onSave={async (next) => {
        await updateExpenditure({ row: e, amount: parseAmountDraft(next) });
      }}
    />
  );
}

/**
 * The inline "repays loan" editor for an expenditure — the mirror of the settles-debt editor
 * below, sending `updateLoanLink`. Only mounted on an editable (non-locked) expenditure; the picker self-hides
 * when the campaign has no loans.
 *
 * In Louisiana this is load-bearing rather than convenient: a repayment files as a Schedule B
 * payment beneath the loan it repays, so an unlinked one cannot file and preflight hard-fails.
 * The link column shipped additive with no backfill, which makes this the one surface where a
 * treasurer can rescue a repayment recorded before it existed.
 */
function ExpenditureRepaysLoanField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
}) {
  const { mutateAsync: updateExpenditure } = useUpdateExpenditure();
  const [error, setError] = useState<string | null>(null);
  const e = item.expenditure;

  const handleChange = async (loanId: string | null) => {
    if (loanId === (e.loanId ?? null)) return;
    setError(null);
    try {
      await updateExpenditure({ row: e, updateLoanLink: true, loanId });
    } catch (err) {
      setError(expenditureEditErrorMessage(err));
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <RepaysLoanPicker
        value={e.loanId}
        onChange={(v) => void handleChange(v)}
        id={`triage-repays-loan-${item.id}`}
        hideLabel
      />
      {error && (
        <p role="alert" data-testid={`triage-ledger-loan-error-${item.id}`} className="text-xs text-rose-700">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * ADR-031 (#92) — the inline "settles debt" editor for an expenditure. Re-tags or untags the
 * debt this expenditure pays down (`Expenditure.DebtId`) by sending `updateDebtLink`. Only mounted on an editable (non-locked) expenditure; the
 * picker self-hides when the campaign has no debts, so it appears only where it's useful.
 */
function ExpenditureSettlesDebtField({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
}) {
  const { mutateAsync: updateExpenditure } = useUpdateExpenditure();
  const [error, setError] = useState<string | null>(null);
  const e = item.expenditure;

  const handleChange = async (debtId: string | null) => {
    if (debtId === (e.debtId ?? null)) return;
    setError(null);
    try {
      await updateExpenditure({ row: e, updateDebtLink: true, debtId });
    } catch (err) {
      setError(expenditureEditErrorMessage(err));
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <SettlesDebtPicker
        value={e.debtId}
        onChange={(v) => void handleChange(v)}
        id={`triage-settles-debt-${item.id}`}
        hideLabel
      />
      {error && (
        <p role="alert" data-testid={`triage-ledger-debt-error-${item.id}`} className="text-xs text-rose-700">
          {error}
        </p>
      )}
    </div>
  );
}

function detailFields(
  item: TriageItem,
  offersEventDates: boolean,
): { label: string; value: ReactNode }[] {
  switch (item.kind) {
    case 'Contribution': {
      return [
        { label: 'Date', value: <ContributionDateField item={item} /> },
        // The designation (Individual / PAC / Corporate / …) is now editable —
        // a treasurer fixes a mis-imported type here (the picker locks on a
        // Reported row and falls back to the plain type label).
        { label: 'Transaction type', value: <ContributionDesignationField item={item} /> },
        // The contributor name is editable, including a person↔organization
        // switch (locks to plain text on a Reported row).
        { label: 'Contributor', value: <ContributionContributorField item={item} /> },
        { label: 'Amount', value: <ContributionAmountField item={item} /> },
      ];
    }
    case 'Expenditure': {
      return [
        { label: 'Date', value: <ExpenditureDateField item={item} /> },
        // The dates a pre-election expenditure notice runs from, where the jurisdiction has one.
        ...(offersEventDates
          ? [
              { label: 'Authorized', value: <ExpenditureEventDateField item={item} which="authorized" /> },
              { label: 'Incurred', value: <ExpenditureEventDateField item={item} which="incurred" /> },
            ]
          : []),
        { label: 'Type', value: kindLabel(item.kind) },
        { label: 'Payee', value: <ExpenditurePayeePurposeField item={item} /> },
        { label: 'Category', value: <ExpenditureCategoryField item={item} /> },
        { label: 'Amount', value: <ExpenditureAmountField item={item} /> },
        // ADR-031 — tag/untag the debt this expenditure settles. Editable rows only (a filed
        // payment is edit-locked); the picker self-hides when the campaign has no debts.
        ...(moneyRowAffordances(item.freeze).edit
          ? [{ label: 'Settles debt', value: <ExpenditureSettlesDebtField item={item} /> }]
          : []),
        // Tag/untag the loan this expenditure repays. Editable rows only; the picker self-hides
        // when the campaign has no loans. This is the remediation surface for Louisiana's
        // unlinked-repayment hard stop: the LoanId column shipped with no backfill, so every
        // repayment recorded before it existed is unlinked and blocks filing until linked here.
        ...(moneyRowAffordances(item.freeze).edit
          ? [{ label: 'Repays loan', value: <ExpenditureRepaysLoanField item={item} /> }]
          : []),
        // ADR-020 post-upload true-up copy, on a locked fee row. Treasurer-first: no enum names.
        ...(moneyRowAffordances(item.freeze).lock !== null && item.expenditure.feeProcessorId != null
          ? [{
              label: 'Statement differs?',
              value: (
                <span className="text-muted-foreground text-xs leading-relaxed">
                  If the statement is higher, book the difference as a new entry dated
                  today — it rides the next upload. If lower, correct it in the state
                  portal (the portal is add-only; a re-upload would create a duplicate).
                </span>
              ),
            }]
          : []),
      ];
    }
    case 'Refund': {
      return [
        { label: 'Date', value: <RefundDateField item={item} /> },
        { label: 'Type', value: kindLabel(item.kind) },
        { label: 'Reason', value: <RefundReasonField item={item} /> },
        { label: 'Amount', value: <RefundAmountField item={item} /> },
      ];
    }
    case 'BankLine': {
      const { bt } = item;
      // Every bank field here is display-only. A bank line is editable ONLY when
      // status === 'NeedsAction' — but that is exactly when its classification
      // panel shows, and a panel suppresses this detail list (hasActionPanel),
      // so the inline description/amount editors could never be reached. We
      // removed them: a wrong bank amount is corrected in a future classify-flow
      // affordance, not here. Date/Account were already the imported statement
      // record (the bank PATCH has no date endpoint). Date/Amount are monospace
      // to match the register cells; Description stays sans.
      return [
        { label: 'Date', value: <span className="font-mono">{formatDate(bt.date)}</span> },
        { label: 'Type', value: kindLabel(item.kind) },
        { label: 'Description', value: bt.description },
        { label: 'Amount', value: <span className="font-mono">{formatAmount(bt.amount)}</span> },
        { label: 'Account', value: bt.bankAccountName },
        // ADR-014 exclusion rationale — when a line was excluded from the report
        // with a note (the D7 one-click "already booked" exclude prefills one,
        // e.g. "matches the fee booked 6/30"; a manual exclude can add its own),
        // surface WHY the line was pushed off the report. Only excluded rows carry
        // a note, so the field only appears when present.
        ...(bt.excludeNote
          ? [{
              label: 'Excluded because',
              value: (
                <span className="text-muted-foreground leading-relaxed">
                  {bt.excludeNote}
                </span>
              ),
            }]
          : []),
      ];
    }
    default:
      return assertNever(item);
  }
}

/**
 * Single source of truth for "does this row mount an action panel?" — the bank
 * classification panel, the missing-donor-info fill, the missing-payee-info
 * editor, or the orphan-refund parent matcher. When a panel is up, the expanded
 * row suppresses its read-only detail list (RowDetailSummary): the panel already
 * states the row's date/amount/etc., and restating them below in a different
 * visual language is redundant and incoherent (a NeedsAction bank row otherwise
 * showed its amount three times). This predicate gates BOTH the detail list and
 * ExpandedBody so the two can never drift — the conditions are identical to the
 * sub-cases under which ExpandedBody renders a non-null panel.
 */
// A contribution row carries the soft person-vs-org shape nudge (the amber "Looks like a PAC" /
// "Maybe a person — verify." pills). Unlike the required actions, the nudge is advisory and
// orthogonal to `actionNeeded` — so it opens the two-choice ContributorShapeNudgeBody (switch the
// shape, or dismiss the reminder) only when no higher-priority required panel already applies.
function hasShapeNudge(item: TriageItem): boolean {
  return (
    item.kind === 'Contribution' &&
    (item.contribution.suggestsNonIndividualType === true ||
      item.contribution.suggestsMissingLastName === true)
  );
}

function hasActionPanel(item: TriageItem): boolean {
  switch (item.kind) {
    case 'BankLine':
      return item.status === 'NeedsAction';
    case 'Contribution':
      return (
        item.actionNeeded === 'ProvideDonorInfo' ||
        item.actionNeeded === 'ResolveContributorShape' ||
        item.actionNeeded === 'ClassifyOrganization' ||
        hasShapeNudge(item)
      );
    case 'Expenditure':
      return item.actionNeeded === 'ProvideExpenditureInfo';
    case 'Refund':
      return item.status === 'NeedsAction';
    default:
      return assertNever(item);
  }
}

// The manual "mark as reported" carve-out flips a genuinely-Confirmed money row
// to Reported (the treasurer filed it directly in the AL portal). It must NOT be
// offered on a row whose stamped ledgerStatus is ALREADY 'Reported' — the backend
// only accepts Confirmed → Reported and 400s otherwise ("Only a confirmed (Done)
// transaction can be marked as reported"). A row filed here, then left in an OPEN
// period, keeps a Done render state (ADR-019 D6) yet carries ledgerStatus:'Reported',
// so `status === 'Done'` alone does not gate it — we check the stamped status.
function canOfferMarkAsReported(
  item: Extract<TriageItem, { kind: 'Contribution' | 'Expenditure' }>,
): boolean {
  const ledgerStatus =
    item.kind === 'Contribution'
      ? item.contribution.ledgerStatus
      : item.expenditure.ledgerStatus;
  return ledgerStatus === 'Confirmed';
}

function ExpandedBody({
  item,
  onCollapse,
  onClassifiedFollowUp,
}: {
  item: TriageItem;
  onCollapse: () => void;
  onClassifiedFollowUp?: (newEntityId?: string) => void;
}) {
  const queryClient = useQueryClient();

  // The guard guarantees each kind's panel condition below, so the inner
  // renders are unconditional (no per-kind ternary needed).
  if (!hasActionPanel(item)) return null;

  switch (item.kind) {
    case 'BankLine': {
      const { bt } = item;
      const isCredit = bt.amount >= 0;
      return (
        <div className="flex flex-col gap-3">
          {/* Variant C (#575) — the jurisdiction-aware amend guidance for a boundary straggler. It
              reads the SERVED amendmentModel (no isAlabama boolean), frames the amend as the EXISTING
              classify → per-jurisdiction path, and offers the exclude off-ramp. Renders only when this
              line is a straggler (bt.inClosedPeriod). */}
          {bt.inClosedPeriod && <StragglerAmendHint />}
          {/* Money-in is always a Contribution, money-out always an Expense —
              so the panel is mounted sign-based (no contribution-vs-expense
              toggle). Because that toggle is hidden, the non-reporting off-ramp
              ("Exclude from report", ADR-014) can't be reached from inside the
              panel — so it's surfaced as the muted secondary action below (a
              Numero payout / transfer / personal charge is recorded here without
              collapsing back to the row-level trailing ✕). */}
          <BankTransactionClassificationPanel
            row={{
              id: bt.id,
              date: bt.date,
              amount: bt.amount,
              description: bt.description,
              sourceLabel: bt.bankAccountName,
              concurrencyVersion: bt.concurrencyVersion,
              suggestedContactId: null,
              suggestedPaymentMethod: null,
              suggestedReferenceNumber: null,
            }}
            initialSelectedType={isCredit ? 'contribution' : 'expenditure'}
            hideTypeToggle
            classificationHint={bt.classificationHint}
            onClose={onCollapse}
            onClassified={(newEntityId) => {
              queryClient.invalidateQueries({
                queryKey: queryKeys.finance.sortInboxAll(),
              });
              // Hand the new entity's id up so the ledger opens it if it still
              // needs work; fall back to a plain collapse if unwired.
              if (onClassifiedFollowUp) onClassifiedFollowUp(newEntityId);
              else onCollapse();
            }}
          />
          <BankLineDetailExcludeAction item={item} onCollapse={onCollapse} />
        </div>
      );
    }
    case 'Contribution':
      // Issue #322 — a person-vs-org type/kind contradiction opens the inline
      // contributor-shape editor right here in the expanded details, so the treasurer
      // fixes the shared donor (kind + name) without leaving for a contact page.
      if (item.actionNeeded === 'ResolveContributorShape') {
        return (
          <ContributorShapeBody
            item={item}
            contactConcurrencyVersion={item.contribution.contactConcurrencyVersion}
            onClassified={onCollapse}
          />
        );
      }
      // ADR-057 — an AL organization donor missing its subtype opens the inline
      // organization-type picker right here, so the treasurer classifies it without
      // leaving for the Contacts page (the gap the ledger signal was added to close).
      if (item.actionNeeded === 'ClassifyOrganization') {
        return (
          <ClassifyOrganizationBody
            item={item}
            contactConcurrencyVersion={item.contribution.contactConcurrencyVersion}
            onClassified={onCollapse}
          />
        );
      }
      // A required donor-info fill (itemized donor missing occupation/employer). The
      // fill panel offers only two compliant choices, both in the body: enter
      // occupation/employer, or record that we asked (FEC best efforts, 11 CFR 104.7 —
      // the row then files as "Information Requested"). No bare "confirm anyway": that
      // would approve an itemized donor with blank occ/emp AND no best-efforts record,
      // the one path to a deficient filing. (Federal-only surface — AL declares
      // employerOccupationRequired = false, so it never reaches here.) This required
      // action wins over the soft shape nudge below (MissingInfoBody carries its own
      // shape-switch escape hatch), so a donor-info row still fills as it does today.
      if (item.actionNeeded === 'ProvideDonorInfo') {
        return (
          <MissingInfoBody
            item={item}
            // The REAL donor-contact version (now required on the wire). A
            // hardcoded `0` / `?? 0` 409s for any donor contact touched before,
            // silently blocking the period close.
            contactConcurrencyVersion={item.contribution.contactConcurrencyVersion}
            onClassified={onCollapse}
            onRegisterConfirm={noopRegister}
            onRegisterRequestDonorInfo={noopRegister}
          />
        );
      }
      // Otherwise the guard (hasActionPanel) guarantees the only remaining reason this row
      // opened a panel is the soft shape nudge — a Confirm / Done / Reported row carrying the
      // amber "Looks like a PAC" / "Maybe a person — verify." pill. The nudge has no required
      // action: the treasurer either switches the shape or dismisses the false-positive.
      return <ContributorShapeNudgeBody item={item} onCollapse={onCollapse} />;
    case 'Expenditure':
      // Guard guarantees actionNeeded === 'ProvideExpenditureInfo' — the payee
      // address editor. One primary "Save & confirm" (fill + confirm); the
      // Confirm-as-draft escape is demoted inside that body's footer (the
      // file-time payee-address hard-fail remains the backstop). Keyed on the
      // payee contact id so re-pointing the row at a different payee remounts the
      // body with the new payee's (prefilled) address instead of carrying the old
      // payee's typed values into the new contact's PUT.
      return (
        <ExpenditurePayeeInfoBody
          key={item.expenditure.payeeContactId}
          item={item}
          onSaved={onCollapse}
        />
      );
    case 'Refund':
      // Guard guarantees status === 'NeedsAction' — an orphan refund showing the
      // parent-matching body.
      return (
        <OrphanRefundBody
          item={item}
          onClassified={onCollapse}
          onRegisterConfirm={noopRegister}
          onRegisterPickParent={noopRegister}
          onRegisterStandalone={noopRegister}
        />
      );
    default:
      return assertNever(item);
  }
}

/**
 * AL EDI delta-export (design §6) — the manual one-click "mark as reported"
 * action inside a Done money row's expansion. A treasurer who entered the
 * transaction directly in the AL FCPA portal flips it Confirmed → Reported so
 * our rules see it WITHOUT us re-exporting it (a re-export would duplicate it on
 * the append-only portal — ADR-020). An audited row mutation; an optional note
 * lives in the panel; after the flip the leading control re-renders as the inert
 * Reported lock and an ephemeral inline "Undo" appears (the undo is a real,
 * audited, provenance-guarded endpoint — the inline affordance is navigation-only,
 * not persisted). The visible lock + the undo are the safety, not a gate.
 */
function MarkAsReportedAction({
  item,
}: {
  item: Extract<TriageItem, { kind: 'Contribution' | 'Expenditure' }>;
}) {
  const { errorMessage, run, errorId, isRunning } = useQuickAction(`${item.id}:mark`);
  const { mutateAsync: markContribAsync, isPending: markContribPending } =
    useMarkContributionReported();
  const { mutateAsync: unmarkContribAsync, isPending: unmarkContribPending } =
    useUnmarkContributionReported();
  const { mutateAsync: markExpAsync, isPending: markExpPending } =
    useMarkExpenditureReported();
  const { mutateAsync: unmarkExpAsync, isPending: unmarkExpPending } =
    useUnmarkExpenditureReported();
  // Jurisdiction-aware copy — names THIS campaign's filing destination (AL FCPA
  // portal, VA COMET, …) instead of hardcoding "the AL portal"; federal stays
  // neutral. Same filing-model source every other finance surface reads.
  const portal = usePortalUploadModel();
  const copy = markReportedElsewhereCopy(portal);
  const [note, setNote] = useState('');
  // Progressive disclosure: this is the RARE action (a treasurer who filed a
  // transaction directly, outside the app), far less common than the trailing
  // Exclude ✕. So it stays a quiet one-line trigger until engaged, instead of an
  // always-open note-field panel that outweighs the more-used actions.
  const [expanded, setExpanded] = useState(false);
  // Whether to show the inline Undo. Kept in a row-keyed store (not local state) so the
  // optimistic Reported flip — which flips `canOfferMarkAsReported` false — can't
  // unmount this component out from under the Undo before the treasurer sees it.
  const justMarked = useIsJustMarkedReported(item.id);
  const setJustMarked = useJustMarkedReportedStore((s) => s.setJustMarked);
  const clearJustMarked = useJustMarkedReportedStore((s) => s.clearJustMarked);

  const isPending =
    markContribPending ||
    unmarkContribPending ||
    markExpPending ||
    unmarkExpPending ||
    // Row-scoped in-flight flag: survives the optimistic control swap so a rapid
    // mark/unmark toggle can't fire an overlapping stale-version POST.
    isRunning;

  // Mark-as-reported only makes sense for an append-only INCREMENTAL portal (AL):
  // there a Reported row drops out of the next delta export so it isn't re-uploaded
  // and duplicated (ADR-020). On a whole-report jurisdiction (federal, VA) a live
  // filing includes Confirmed rows ONLY, so marking a row would silently drop it from
  // the next report — the backend rejects it too (ManualReportMarkService). Don't
  // offer it. #571 — keyed on the served append-only axis (`amendmentModel`), never an isAlabama boolean.
  if (portal.amendmentModel !== AmendmentModel.PortalOnly) return null;

  const handleMark = () =>
    run(async () => {
      const trimmed = note.trim();
      const noteArg = trimmed === '' ? undefined : trimmed;
      if (item.kind === 'Contribution') {
        await markContribAsync({ row: item.contribution, note: noteArg });
      } else {
        await markExpAsync({ row: item.expenditure, note: noteArg });
      }
      setJustMarked(item.id);
    }, 'Failed to mark this as reported.');

  const handleUndo = () =>
    run(async () => {
      if (item.kind === 'Contribution') {
        await unmarkContribAsync({ row: item.contribution });
      } else {
        await unmarkExpAsync({ row: item.expenditure });
      }
      clearJustMarked(item.id);
    }, 'Failed to undo the mark-reported.');

  if (justMarked) {
    return (
      <div
        data-testid={`triage-ledger-mark-reported-ack-${item.id}`}
        className="flex items-center gap-2 border-t pt-3 text-sm"
      >
        <span className="text-muted-foreground">Marked as reported.</span>
        <button
          type="button"
          data-testid={`triage-ledger-undo-mark-reported-${item.id}`}
          disabled={isPending}
          onClick={handleUndo}
          className={cn(financeTextActionClass, 'font-medium text-primary hover:bg-primary/10 hover:underline')}
        >
          Undo
        </button>
        <RowError id={errorId} message={errorMessage} onRetry={handleUndo} />
      </div>
    );
  }

  // Collapsed: a quiet, self-selecting line. A treasurer who did NOT file this
  // directly reads the question and moves on; only the rare one who did opens it.
  if (!expanded) {
    return (
      <div className="flex justify-end border-t pt-3">
        <button
          type="button"
          data-testid={`triage-ledger-mark-reported-trigger-${item.id}`}
          onClick={() => setExpanded(true)}
          className={cn(financeTextActionClass, 'text-muted-foreground hover:bg-muted/60 hover:text-foreground hover:underline')}
        >
          {copy.trigger} Mark as reported
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      {/* The distinguishing explanation, in context — it stays ON the report
          (unlike the trailing Exclude ✕, which takes it off). */}
      <p className="text-xs text-muted-foreground">{copy.helper}</p>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`triage-ledger-mark-reported-note-input-${item.id}`}
          className="text-xs text-muted-foreground"
        >
          Note (optional)
        </label>
        <input
          id={`triage-ledger-mark-reported-note-input-${item.id}`}
          data-testid={`triage-ledger-mark-reported-note-${item.id}`}
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={copy.placeholder}
          className={financeFormInputClass}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          data-testid={`triage-ledger-mark-reported-${item.id}`}
          disabled={isPending}
          onClick={handleMark}
        >
          Mark as reported
        </Button>
        <Button
          type="button"
          variant="ghost"
          data-testid={`triage-ledger-mark-reported-cancel-${item.id}`}
          disabled={isPending}
          onClick={() => setExpanded(false)}
        >
          Cancel
        </Button>
      </div>
      <RowError id={errorId} message={errorMessage} onRetry={handleMark} />
    </div>
  );
}

/**
 * Maps a reopen failure to treasurer copy — switches on the two wire codes (no
 * enum identifiers leak), with the standard stale-row message for a 409.
 */
function reopenErrorMessage(err: unknown): string {
  if (isStaleWrite(err)) return SAVE_FAILED_MESSAGE;
  if (err instanceof BankTransactionClassifyError) {
    if (err.code === ErrorCode.BankTransactionReopenNotReopenable) {
      return "This row can't be reopened from here.";
    }
    if (err.code === ErrorCode.BankTransactionReopenAlreadyClassified) {
      return 'This row was already classified — refresh to see its current state.';
    }
  }
  return err instanceof Error ? err.message : 'Failed to reopen this row.';
}

/**
 * Maps an expenditure unconfirm failure to treasurer copy — switches on the
 * backend wire strings (no enum identifiers leak), with the standard stale-row
 * message for a 409.
 */
function unconfirmExpenditureErrorMessage(err: unknown): string {
  if (isStaleWrite(err)) return SAVE_FAILED_MESSAGE;
  if (err instanceof ExpenditureConfirmError) {
    // Only the server knows whether an in-app report covers the locked date, so it owns the wording.
    if (err.code === ErrorCode.ExpenditureUnconfirmIsReported) return err.message;
    if (err.code === ErrorCode.ExpenditureUnconfirmNotConfirmed) {
      return 'This expenditure was already updated — refresh to see its current state.';
    }
  }
  return err instanceof Error ? err.message : 'Failed to uncheck this expenditure.';
}

/**
 * Maps an expenditure confirm failure to treasurer copy — switches on the
 * backend wire strings (no enum identifiers leak).
 */
function confirmExpenditureErrorMessage(err: unknown): string {
  if (isStaleWrite(err)) return SAVE_FAILED_MESSAGE;
  if (err instanceof ExpenditureConfirmError) {
    if (err.code === ErrorCode.ExpenditureConfirmIsReported) return err.message;
    if (err.code === ErrorCode.ExpenditureConfirmNotPending) {
      return 'This expenditure was already updated — refresh to see its current state.';
    }
  }
  return err instanceof Error ? err.message : 'Failed to confirm this expenditure.';
}

/**
 * The inline payee-address editor for a `ProvideExpenditureInfo` expenditure
 * (above-threshold, payee not yet itemizable). PUTs the four address fields
 * onto the payee `FinanceContact`. A full address makes the payee itemizable,
 * so the next sort-inbox refetch drops the row to Done.
 */
function ExpenditurePayeeInfoBody({
  item,
  onSaved,
}: {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
  onSaved: () => void;
}) {
  const { expenditure } = item;
  const [address, setAddress] = useState(expenditure.payeeAddress ?? '');
  // Optional suite/apt line — prefilled so a save never blanks an existing
  // value; never gates canSave.
  const [addressLine2, setAddressLine2] = useState(expenditure.payeeAddress2 ?? '');
  const [city, setCity] = useState(expenditure.payeeCity ?? '');
  const [state, setState] = useState(expenditure.payeeState ?? '');
  const [zipCode, setZipCode] = useState(expenditure.payeeZipCode ?? '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The payee itself may be wrong (not just missing an address). Let the treasurer
  // re-point the expenditure at a different contact right here — if that contact
  // already has a full address the row itemizes and drops to Done, so they never
  // have to type an address for the wrong payee just to change it.
  const [pickingVendor, setPickingVendor] = useState(false);

  const { mutateAsync: updateContactAsync, isPending: savePending } =
    useUpdateFinanceContact();
  // Fill-and-confirm: filling the payee address IS the treasurer's sign-off, so
  // a still-Pending (bank-classified / captured) expenditure is confirmed in the
  // same inline gesture and drops straight to Done — no redundant standalone
  // Confirm. The ledger sibling of ExpenditureBody's fold-in.
  const { mutateAsync: confirmExpenditureAsync, isPending: confirmPending } =
    useConfirmExpenditure();
  // Reassign the payee. The
  // sort-inbox refetch drives the rest: a payee WITH an address drops this row to
  // Done (this body unmounts); one still missing an address resurfaces here,
  // re-pointed at the new payee (the ExpandedBody key remounts it, resetting the
  // prefilled fields so the old payee's street never lands on the new contact).
  // Shared with the normal payee editor via `useReassignPayee` (errors surface in
  // this body's own message box).
  const { reassign, isReassigning } = useReassignPayee(expenditure, setErrorMessage);
  const isPending = savePending || confirmPending || isReassigning;

  const canSave =
    address.trim() !== '' &&
    city.trim() !== '' &&
    state.trim() !== '' &&
    zipCode.trim() !== '';

  const handleSave = async () => {
    setErrorMessage(null);
    try {
      await updateContactAsync({
        id: expenditure.payeeContactId,
        data: updateFinanceContactRequest({
          address: address.trim(),
          addressLine2: addressLine2.trim() || null,
          city: city.trim(),
          state: state.trim(),
          zipCode: zipCode.trim(),
          // The payee contact's real version — a hardcoded 0 409s for any
          // payee touched before, silently blocking the period close.
          concurrencyVersion: expenditure.payeeContactConcurrencyVersion,
        }),
      });
      // The contact PUT doesn't bump the expenditure version, so its own token
      // is still valid. Only a still-Pending row needs the flip; an already-
      // Confirmed-but-incomplete row just became Complete and drops to Done.
      if (expenditure.ledgerStatus === 'Pending') {
        await confirmExpenditureAsync({
          row: expenditure,
        });
      }
      onSaved();
    } catch (err) {
      if (
        err instanceof FinanceContactUpdateError && isStaleWrite(err)
      ) {
        setErrorMessage(REFRESH_PROMPT);
        return;
      }
      // The address saved but the fold-in confirm failed — the address IS
      // persisted, so this degrades to the row resurfacing as a standalone
      // Confirm. Reuse the confirm→treasurer-copy mapper (no enum leak).
      if (err instanceof ExpenditureConfirmError) {
        setErrorMessage(confirmExpenditureErrorMessage(err));
        return;
      }
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to update the payee address.',
      );
    }
  };

  return (
    <div
      data-testid="expenditure-payee-info-body"
      className="flex flex-col gap-4"
    >
      <div className="rounded-md border bg-muted/30 p-3 text-sm">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-xs text-muted-foreground">
            {expenditure.date}
          </span>
          <span className="font-semibold">{formatAmount(expenditure.amount)}</span>
          {!pickingVendor && (
            <button
              type="button"
              data-testid="expenditure-payee-change"
              className={cn(financeInlineEditTriggerClass, 'shrink-0 cursor-pointer')}
              aria-label="Change payee"
              title="Change payee"
              disabled={isPending}
              onClick={() => setPickingVendor(true)}
            >
              {expenditure.payeeName}
              <InlineEditIcon
                saving={isReassigning}
                savingTestId="expenditure-payee-change-saving"
              />
            </button>
          )}
        </div>
        {pickingVendor && (
          <div className="mt-2">
            <ExpenditureVendorPicker
              id={item.id}
              onSelect={(contactId) => {
                setPickingVendor(false);
                void reassign(contactId);
              }}
              onCancel={() => setPickingVendor(false)}
            />
          </div>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          Wrong payee? Change it above. Otherwise add the missing address fields
          below to finish.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Street address</span>
          <AddressSuggestInput
            data-testid="expenditure-payee-address"
            value={address}
            disabled={isPending}
            className={financeFormInputClass}
            onChange={setAddress}
            onPick={(p) => {
              setCity(p.city);
              setState(p.state);
              setZipCode(p.zip);
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Address line 2</span>
          <input
            data-testid="expenditure-payee-address2"
            type="text"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            disabled={isPending}
            className={financeFormInputClass}
            placeholder="Apt, suite, floor (optional)"
          />
        </label>
        <div className="flex gap-3">
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="font-medium">City</span>
            <input
              data-testid="expenditure-payee-city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={isPending}
              className={financeFormInputClass}
            />
          </label>
          <label className="flex w-20 flex-col gap-1 text-sm">
            <span className="font-medium">State</span>
            <input
              data-testid="expenditure-payee-state"
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              disabled={isPending}
              className={financeFormInputClass}
            />
          </label>
          <label className="flex w-28 flex-col gap-1 text-sm">
            <span className="font-medium">ZIP</span>
            <input
              data-testid="expenditure-payee-zip"
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              disabled={isPending}
              className={financeFormInputClass}
            />
          </label>
        </div>
      </div>

      {errorMessage && (
        <div
          role="alert"
          data-testid="expenditure-payee-error"
          className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {errorMessage}
        </div>
      )}

      {/* Full-width primary so the finish action spans the panel and the
          treasurer can't miss it in the bottom-right after typing the address.
          "Save & confirm" names both effects: the address is persisted AND the
          row is finalized in one gesture. */}
      <div className="border-t pt-3">
        <Button
          type="button"
          className="w-full"
          data-testid="expenditure-payee-save"
          disabled={isPending || !canSave}
          onClick={handleSave}
        >
          Save & confirm
        </Button>
      </div>
    </div>
  );
}


/**
 * The non-binding deposit hint on a NeedsAction credit bank row: recorded
 * checks awaiting deposit within the 14-day pre-deposit window. A bold summary
 * (what we noticed), a muted plain-language instruction (what to do), then the
 * named + linked list of the actual check entries it references (#597) so the
 * treasurer clicks straight through instead of hunting. Rendered as a wrapping
 * block, not a single-line pill, so it stays fully visible at font-scale 1.3
 * for elderly treasurers — a tooltip would hide it. The action is Exclude from
 * report; the recorded checks report on their own (the "Deposit of recorded
 * checks" classifier was retired 2026-07-14).
 *
 * When the checks total does NOT equal the deposit (#597 — e.g. checks total
 * $500 vs a smaller $360 deposit) the copy softens from "this deposit IS those
 * checks" to "some of these checks may make up this deposit", so a mismatched
 * pairing reads honestly instead of as a contradiction.
 */
function DepositHintChip({
  id,
  amount,
  hint,
}: {
  id: string;
  amount: number;
  hint: BankTransactionDepositHintDto;
}) {
  // Compare in whole cents so a JSON float round-trip can't make an exact
  // checks-total-vs-deposit pairing read as a mismatch (or vice-versa).
  const totalMatchesDeposit =
    Math.round(hint.total * 100) === Math.round(amount * 100);
  return (
    <div
      data-testid={`triage-ledger-deposit-hint-${id}`}
      className="w-fit max-w-prose rounded-md bg-primary/10 px-2 py-1 text-xs text-primary"
    >
      <p className="font-semibold">
        You recorded {hint.count} {hint.count === 1 ? 'check' : 'checks'} totaling{' '}
        {formatAmount(hint.total)}{' '}
        {totalMatchesDeposit
          ? 'just before this deposit.'
          : `in the two weeks before this ${formatAmount(amount)} deposit.`}
      </p>
      <p className="mt-0.5 font-normal text-primary/80">
        {totalMatchesDeposit
          ? 'If this deposit is those checks, confirm each check in your ledger, then exclude this line from the report — the checks report on their own.'
          : 'If some of these checks make up this deposit, confirm each one in your ledger, then exclude this line from the report — the checks report on their own.'}
      </p>
      <ul className="mt-1 space-y-0.5">
        {hint.checks.map((check) => (
          <li key={check.contributionId}>
            <Link
              to="/admin/finance/contacts/$contactId"
              params={{ contactId: check.contactId }}
              className="font-medium hover:underline underline-offset-2"
            >
              {check.contributorName || 'Unnamed contributor'}
            </Link>
            {' — '}
            {formatAmount(check.amount)} on {formatDate(check.date)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The captured check image inside a contribution's ledger expansion. Mounts
 * only when the row is expanded, keyed off the contribution's `checkImage`.
 */
function ContributionCheckImage({
  contributionId,
  fileName,
}: {
  contributionId: string;
  fileName: string;
}) {
  const { data, isLoading, isError } = useContributionCheckImage(
    contributionId,
    true,
  );
  // Click the inline preview to open a full-size lightbox — the small preview is
  // rarely enough to read a memo line or verify the amount. The button is an
  // interactive target, so `isInteractiveTarget` keeps the click from also
  // toggling the row's expand/collapse.
  const [enlarged, setEnlarged] = useState(false);
  return (
    <div className="mb-3">
      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading check image…</p>
      )}
      {isError && (
        <p className="text-sm text-rose-700">Couldn&apos;t load the check image.</p>
      )}
      {data && (
        <>
          <button
            type="button"
            onClick={() => setEnlarged(true)}
            aria-label={`Enlarge captured check (${fileName})`}
            className="block cursor-zoom-in rounded border transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img
              data-testid={`triage-ledger-check-image-${contributionId}`}
              src={data.url}
              alt={`Captured check (${fileName})`}
              className="max-h-80 w-auto rounded"
            />
          </button>
          <Dialog open={enlarged} onOpenChange={setEnlarged}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Captured check</DialogTitle>
                <DialogDescription>{fileName}</DialogDescription>
              </DialogHeader>
              <img
                data-testid={`triage-ledger-check-image-enlarged-${contributionId}`}
                src={data.url}
                alt={`Captured check (${fileName})`}
                className="max-h-[80vh] w-auto rounded"
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
